//#region Imports
import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
//oneFront SDK
import {
  useSnackbar,
  useAuth,
  useQuery,
  useMutation,
  useGet,
  usePost,
  useTranslation,
} from "@1f/react-sdk";

//Vapor Components
import VaporTag from "@vapor/react-custom/VaporTag";
import VaporPage from "@vapor/react-custom/VaporPage";
import Grid from "@vapor/react-material/Grid";
import Button from "@vapor/react-material/Button";
import Tooltip from "@vapor/react-material/Tooltip";
import ExtendedTabs from "@vapor/react-extended/ExtendedTabs";
import ExtendedTab from "@vapor/react-extended/ExtendedTab";
//Custom components
import { Loading } from "../../components/Loading";
import { NotAuth } from "../../components/NotAuth";
import { ModalDelete } from "../../components/ModalDelete";
import { ModalPdfViewer } from "../../components/ModalPdfViewer";
import ModalBancaDatiDetail from "../../components/ModalBancaDatiDetail";
import { FascicoloDrawer } from "../../components/fascicolo/FascicoloDrawer";
import { AnagraficaForm } from "../../components/anagrafica/AnagraficaForm";
import { PrestazioneForm } from "../../components/prestazione/PrestazioneForm";
import { NewRischioForm } from "../../components/rischio/NewRischioForm";
import { IndicatoreAnomaliaForm } from "../../components/indicatore_anomalia/IndicatoreAnomaliaForm";
//Constants, Api and Data Models
import {
	bsaFascicolo,
	bsaSegnalazione,
} from "../../businessLogic/constants/securityAreas";
import { pathFascicoloAdd } from "../../businessLogic/constants/path";
import {
	TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
	TIPO_ANAGRAFICA_CLIENTE_PG_KEY,
	TIPO_ANAGRAFICA_LEGALE_RAPP_KEY,
	TIPO_ANAGRAFICA_PROCURATORE_KEY,
	TIPO_ANAGRAFICA_TITOLARE_EFFETTIVO_KEY,
} from "../../businessLogic/constants/baseTableKeys";
import {
	FascicoloHelpers,
	isFascicoloCessato,
	mapFascicoloStatoFromBE,
} from "../../businessLogic/helpers/fascicoloHelpers";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import {
	getLocalStorageSelectedFascicolo,
	setLocalStorageSelectedFascicolo,
	removeLocalStorageSelectedFascicolo,
	removeLocalStorageSelectedEntity,
	getLocalStorageSelectedEntity,
} from "../../businessLogic/helpers/storageHelpers";
import {
	userPermissionModel,
	fascicoloModel,
	anagraficaModel,
	prestazioneModel,
	prestazionePagamentoModel,
	rischioModel,	
	bancaDatiModel,
	anagraficaBancaDatiModel,
	sosModel,
	indicatoreAnomaliaModel,
} from "../../businessLogic/models";
import { query_fascicolo_byId } from "../../businessLogic/query/fascicoloQueris";
import { query_anagrafiche_byFascicolo } from "../../businessLogic/query/anagraficaQueries";
import { query_base_all } from "../../businessLogic/query/baseTablesQueries";
import { query_prestazioni_byFascicolo } from "../../businessLogic/query/prestazioniQueries";
import {
	query_rischi_byPrestazione,
	query_rischi_byTenant,
} from "../../businessLogic/query/valutazioneRischioQueries";
import { query_indicatori_anomalia_byFascicolo } from "../../businessLogic/query/indicatoriAnomaliaQueries";
import { mutation_add_audit } from "../../businessLogic/mutations/auditMutations";
import {
	mutation_add_allegato,
	mutation_delete_allegato,
} from "../../businessLogic/mutations/allegatiMutations";
import {
	mutation_add_anagrafica_bancadati,
	mutation_search_legal_entity,
	mutation_search_natural_person,
} from "../../businessLogic/mutations/bancheDatiMutations";
import {
	mutation_add_sos,
	mutation_update_sos,
	mutation_delete_sos,
} from "../../businessLogic/mutations/sosMutations";
import {
	mutation_getInformativaPrivacyReport,
	mutation_getAstensioneProfessionistaReport,
	mutation_getDichiarazioneClienteReport,
	mutation_getDichiarazioneProfessionistaReport,
	mutation_getIstruttoriaClienteReport,
	mutation_getValutazioneRischioReport,
	mutation_getIndicatoriAnomaliaReport,
} from "../../businessLogic/mutations/stampeMutations";
//Style
import { divGreen, divOrange, divRed } from "../../businessLogic/styles";
//Other components
import { v4 as uuidv4 } from "uuid";
import postal from "postal";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
import { AllegatiApi } from "../../businessLogic/api/allegatiApi";
import { EuroConferenceApi } from "../../businessLogic/api/euroconferenceApi";
import { LogApi } from "../../businessLogic/api/logApi";
import { apiErrorHelper } from "../../businessLogic/helpers/apiErrorHelpers";
import BaseTableApi from "../../businessLogic/api/baseTableApi";
import { createFascicoloModel } from "../../businessLogic/helpers/modelHelpers";
import PrestazioniApi from "../../businessLogic/api/prestazioniApi";
import { SosApi } from "../../businessLogic/api/sosApi";
import UserApi from "../../businessLogic/api/userApi";
import { LastActivityApi } from "../../businessLogic/api/lastActivityApi";
import SignatureApi from "../../businessLogic/api/signatureApi";
import { AziendaApi } from "../../businessLogic/api/aziendaApi";
import { useAreaPermissions } from "../../businessLogic/hooks/useAreaPermissions";
//#endregion

const getProfIdsWithActivePrestazioni = (prestazioni) => {
	const now = new Date();
	const ids = new Set();
	prestazioni
		.filter((prest) => prest.EndDate === null || new Date(prest.EndDate) > now)
		.forEach((prest) => (prest.prestazione_users || []).forEach((u) => ids.add(u.UserId)));
	return ids;
};

export const FascicoloAdd = ({ basePath }) => {
	const tenantId = sharedDatas.getPropertyByName("tenantId");
	const aziendaId = sharedDatas.getPropertyByName("aziendaId");
	const aziendaModelList = sharedDatas.getPropertyByName("aziendaModelList");
	const loggedUserId = sharedDatas.getPropertyByName("userId");
	const tenantModel = sharedDatas.getPropertyByName("tenantModel");
	const userModel = sharedDatas.getPropertyByName("userModel");
	const tenantTypeId = sharedDatas.getPropertyByName("userModel").tenantTipoId;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { getPermissions: getFascicoloPermissions } = useAreaPermissions(bsaFascicolo);
	const { getPermissions: getSOSPermissions } = useAreaPermissions(bsaSegnalazione);
	const navigate = useNavigate();
	const entityFromNavigation = getLocalStorageSelectedEntity();
	const euroConferenceApi = EuroConferenceApi();
	const location = useLocation();
	const selectedValue = location.state?.selectedValue || "PG";
	//Helpers
	const fascicoloHelpers = FascicoloHelpers();
	const apiErrorHelpers = apiErrorHelper();
	//Api
	const logApi = LogApi();
	const fascicoloApi = FascicoloApi();
	const allegatiApi = AllegatiApi();
	const baseTableApi = BaseTableApi(); 
	const prestazioneApi = PrestazioniApi();
	const sosApi = SosApi();
	const userApi = UserApi();
	const lastActivityApi = LastActivityApi()
	const signatureApi = SignatureApi()
	const aziendaApi = AziendaApi();
	//#region Query
	const apiBaseAll = useQuery(
        query_base_all,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
	const apiFascicoloById = useQuery(
		query_fascicolo_byId,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiAnagraficaByFascicolo = useQuery(
		query_anagrafiche_byFascicolo,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiPrestazioniByFascicolo = useQuery(
		query_prestazioni_byFascicolo,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiRischiByPrestazione = useQuery(
		query_rischi_byPrestazione,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiRischiByTenant = useQuery(
		query_rischi_byTenant,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiIndicatoriByFascicolo = useQuery(
		query_indicatori_anomalia_byFascicolo,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiPrestByFascicolo = useQuery(
		query_prestazioni_byFascicolo,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	//#endregion
	//#region Mutation
	const apiAddAudit = useMutation(mutation_add_audit, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiAddAllegato = useMutation(mutation_add_allegato, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiAddAnagraficaBancaDati = useMutation(
		mutation_add_anagrafica_bancadati,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiAddSos = useMutation(mutation_add_sos, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiUpdateSos = useMutation(mutation_update_sos, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiDeleteSos = useMutation(mutation_delete_sos, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiSearchLegalEntity = useMutation(mutation_search_legal_entity, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiSearchNaturalPerson = useMutation(mutation_search_natural_person, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiGetInformativaPrivacy = useMutation(
		mutation_getInformativaPrivacyReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetAstensioneProf = useMutation(
		mutation_getAstensioneProfessionistaReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetDichiarazioneCliente = useMutation(
		mutation_getDichiarazioneClienteReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetDichiarazioneProfessionista = useMutation(
		mutation_getDichiarazioneProfessionistaReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetIstruttoriaCliente = useMutation(
		mutation_getIstruttoriaClienteReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetValutazioneRischio = useMutation(
		mutation_getValutazioneRischioReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiGetIndicatoriAnomalia = useMutation(
		mutation_getIndicatoriAnomaliaReport,
		{ graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	//#endregion
	//#region UseStates
	const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
	const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
	const [backDropInternalOpen, setBackDropInternalOpen] = useState(false); //Loader utilizzato durante il caricamento
	const [userPermissions, setUserPermissions] = useState(
		new userPermissionModel(
			0,
			aziendaId,
			bsaFascicolo,
			false,
			false,
			false,
			false,
		),
	);
	const [userPermissionsSOS, setUserPermissionsSOS] = useState(
		new userPermissionModel(
			0,
			aziendaId,
			bsaSegnalazione,
			false,
			false,
			false,
			false,
		),
	);
	//Fascicolo
	const [selFascicolo, setSelFascicolo] = useState(
		new fascicoloModel(
			0,
			tenantId,
			aziendaId,
			"",
			new Date(),
			false,
			new Date(),
			new Date(),
			[],
			"",
			"",
			loggedUserId,
		),
	);
	const [, setSelFascicoloOriginal] = useState(
		new fascicoloModel(
			0,
			tenantId,
			aziendaId,
			"",
			new Date(),
			false,
			new Date(),
			new Date(),
			[],
			"",
			"",
			loggedUserId,
		),
	); //Used for Audit
	const [, setChbVal] = useState({ isPrivate: false });
	//Prestazione
	const [selPrestazione, setSelPrestazione] = useState(new prestazioneModel(0));
	//Professionisti
	const [utentiList, setUtentiList] = useState([]);
	const [filteredUtentiList, setFilteredUtentiList] = useState([]);
	const [activeProfIds, setActiveProfIds] = useState(new Set());
	//Tabelle di base
	const [baseTipoAnagList, setBaseTipoAnagList] = useState([]); //base_tipo_anagrafica
	const [, setBaseTipoDocList] = useState([]); //base_tipo_documento
	const [, setBaseRischioGradoList] = useState([]); //Contiene i gradi di rischio per il tipo di tenant
	const [, setBaseTipoPartList] = useState([]); //base_tipo_partecipazione
	const [, setBaseTipoIdentList] = useState([]); //base_tipo_identificazione
	const baseProvinciaMapRef = useRef(new Map()); //map AddrProvinciaId -> Sigla per formattare gli indirizzi
	//Legali rappresentanti per stampa dicCliente
	const [selectedLegal, setSelectedLegal] = useState([]);
	//Allegati
	const [currentAllegati, setCurrentAllegati] = useState([]);
	const [hasUnavailableAllegati, setHasUnavailableAllegati] = useState(false);
	const [itemId, setItemId] = useState(0);
	//Banca dati
	const [bancaDatiTabValue, setBancaDatiTabValue] = useState("tabBdGratuite");
	const handleChangeBancaDatiTab = (event, newValue) => {
		setBancaDatiTabValue(newValue);
	};
	const [hasBancaDatiAbb] = useState(false);
	const [bancaDatiAbbCredits, setBancaDatiCredits] = useState(0);
	const [bancaDatiAbbProductsList] = useState([]);
	const [bancaDatiAbbProductBought, setBancaDatiAbbProductBought] = useState(false);
	const [bancaDatiAbbResultListNeg, setBancaDatiAbbResultListNeg] = useState({
		BLP: [],
		DEB: [],
		DIB: [],
	});
	const [bancaDatiAbbResultListPar, setBancaDatiAbbResultListPar] = useState({
		PAR1: [],
		PAR2: [],
	});
	const [bancaDatiAbbResultListStr, setBancaDatiAbbResultListStr] = useState({
		CAR: [],
		SOC: [],
		SIN: [],
	});
	const [bancaDatiResult, setBancaDatiResult] = useState({
		Pep_Regionali: false,
		Pep_Metropolitani: false,
		Pep_Provinciali: false,
		Pep_Comunali: false,
		Pep_MaggiorOrgano: false,
		Pep_OrganiStraordinari: false,
		Pep_SindaciInCarica: false,
		Pep_Comune: false,
		UNSC: false,
		OFAC: false,
		UE: false,
		Negativita: false,
		Partecipazioni: false,
		StrutturaSocietaria: false,
	});
	const [anagraficheBancaDatiList, setAnagraficheBancaDatiList] = useState([]); //Contiene i risultati delle banche dati di tutte le anagrafiche
	const [currentAnagraficheList, setCurrentAnagraficheList] = useState([]); //Contiene le anagrafiche con l'ultima versione
	const [currentAnagrafica, setCurrentAnagrafica] = useState(
		new anagraficaModel(
			0,
			0,
			false,
			"",
			"",
			"",
			0,
			"",
			null,
			"",
			"",
			"",
			"",
			"",
			undefined,
			undefined,
			"",
			undefined,
			"",
			"",
			"",
			"",
			undefined,
			undefined,
			"",
			undefined,
			"",
			"",
			"",
			"",
			false,
			false,
			1,
			new Date(),
			false,
			[],
			[],
			"",
			loggedUserId,
		),
	); //Contiene l'anagrafica corrente
	const [currentSOSList, setCurrentSOSList] = useState([]);
	const [currentSOS, setCurrentSOS] = useState(
		new sosModel(
			0,
			new Date(),
			"",
			"",
			"",
			loggedUserId,
			new Date(),
			undefined,
			loggedUserId,
			"",
			null,
		),
	); //Contiene la SOS corrente
	const [currentOriginalSOS, setCurrentOriginalSOS] = useState(
		new sosModel(
			0,
			new Date(),
			"",
			"",
			"",
			loggedUserId,
			new Date(),
			undefined,
			loggedUserId,
			"",
			null,
		),
	); //Contiene la SOS corrente
	const [filteredSOSList, setFilteredSOSList] = useState([]);
	const [sosAllegati, setSosAllegati] = useState([]);
	//Delete Modal
	const [showDeleteModal, setShowDeleteModal] = useState(false); //Modal per la conferma di eliminazione di un record
	const [idToPerform, setIdToPerform] = useState(0); //Contiene l'id del record da visualizzare
	const [typeToPerform, setTypeToPerform] = useState("ALL"); //Contiene il tipo di record da eliminare. ALL: Allegato
	//PDF Viewer Modal
	const [pdfDocuments, setPdfDocuments] = useState([]);
	const [activePdfDocumentId, setActivePdfDocumentId] = useState(null);
	//TS Digital
	const [accessToken] = useState(useAuth().token);
	//#endregion
	//#region Endpoints callers
	const tsApiModeFinanceBuyProduct = usePost(
		{
			headers: {
				Authorization: "Bearer " + accessToken,
				"X-App-Name": "TS420",
				"X-App-Version": "1.0.0",
				"X-Request-ID": uuidv4(),
				"X-Correlation-ID": uuidv4(),
				"Content-Type": "application/json",
				"X-Item-ID": tenantId,
				"X-User-ID": loggedUserId,
			},
		},
		{ lazy: true },
	);
	const tsApiModeFinanceGetDocument = useGet(
		{
			headers: {
				Authorization: "Bearer " + accessToken,
				"X-App-Name": "TS420",
				"X-App-Version": "1.0.0",
				"X-Request-ID": uuidv4(),
				"X-Correlation-ID": uuidv4(),
				"Content-Type": "application/json",
				"X-Item-ID": tenantId,
				"X-User-ID": loggedUserId,
			},
		},
		{ lazy: true },
	);
	//#endregion
	useEffect(() => {
		loadDatas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataLoaded]);
	const handleMissingFascicolo = () => {
		removeLocalStorageSelectedFascicolo();
		enqueueSnackbar("Fascicolo non più disponibile nell'azienda corrente", { variant: "warning" });
		navigate(basePath, { replace: true });
	};
	async function fetchItem() {
		const selFasc = getLocalStorageSelectedFascicolo();
		const fascicoloId = selFasc?.id ?? selFasc;
		if (Number.isInteger(fascicoloId) && fascicoloId > 0) {
			return (
				await fascicoloApi.GetFascicoliAndRelatedEntities(
					tenantId,
					true,
					true,
					fascicoloId,
				)
			)?.fascicoli?.[0];
		}
	}
	async function loadDatas() {
		try {
			if (
				!tenantId || !tenantModel ||
				!aziendaId ||!aziendaModelList ||
				!loggedUserId ||!userModel
			) {
				navigate("/");
			} else {
				setBackDropOpen(true);
				if (!isDataLoaded) {
					const fascicolo = await fetchItem();
					if (!fascicolo) {
						handleMissingFascicolo();
						return;
					}
					setUserPermissions(sharedDatas.getPropertyByName("userPermissions").filter((x) => x.area === bsaFascicolo)[0]);
					setUserPermissionsSOS(sharedDatas.getPropertyByName("userPermissions").filter((x) => x.area === bsaSegnalazione)[0]);
					const utentiList = await loadUsers();
					await loadBaseTables()
					if (fascicolo.id > 0) {		
						await processFascicoloData(fascicolo, utentiList);			
						await lastActivityApi.UpdateLastActivity(pathFascicoloAdd,fascicolo.id.toString(),fascicolo.name);									
					}
					await loadAllegati(fascicolo.id);
					setItemId(fascicolo.id);
					let sos = await loadSos(fascicolo.id);
					const entityFromNavigationResult = await entityFromNavigation;
					let entityFromNavigationResultJson = null;
					if (entityFromNavigationResult !== null) {
						// Controlla se è già un oggetto o se è una stringa da parsare
						if (typeof entityFromNavigationResult === "string") {
							try {
								entityFromNavigationResultJson = JSON.parse(entityFromNavigationResult);
							} catch (parseError) {
								console.warn("Error parsing entityFromNavigation string:",parseError);
								entityFromNavigationResultJson = null;
							}
						} else if (typeof entityFromNavigationResult === "object") {
							// È già un oggetto, usalo direttamente
							entityFromNavigationResultJson = entityFromNavigationResult;
						}
						if (entityFromNavigationResultJson && entityFromNavigationResultJson.entityType) {
							switch (entityFromNavigationResultJson.entityType) {
								case "anagrafica": {
									setSelectedTab("tabAnagrafica");
									break;
								}
								case "prestazione": {
									setSelectedTab("tabPrestazione");
									break;
								}
								case "rischio": {
									setSelectedTab("tabRischio");
									break;
								}
								case "sos": {
									setSelectedTab("tabAnagrafica");
									setOpenDrawer(true);
									setDrawerTabValue("sos");
									setDrawerHeaderTitle("SOS");
									setDrawerHeaderDesc("Segnalazioni di Operazioni Sospette");
									const selectedSos = sos.filter((x) => x.id === entityFromNavigationResultJson.entityId)[0];
									setCurrentSOS(selectedSos);
									setCurrentOriginalSOS(selectedSos);
									break;
								}
								default: {									
									break;
								}
							}
						}
						// Le entita' "anagrafica", "prestazione" e "rischio" vengono
						// consumate (e rimosse) dai rispettivi form figli (AnagraficaForm /
						// PrestazioneForm / NewRischioForm) al loro mount. Se le rimuovessimo
						// qui, il form figlio le troverebbe gia' nulle e ripiegherebbe sulla
						// selezione di default. Quindi qui rimuoviamo solo i tipi che
						// FascicoloAdd consuma direttamente (es. "sos").
						const entityType = entityFromNavigationResultJson?.entityType;
						const consumedByChildForm =
							entityType === "anagrafica" ||
							entityType === "prestazione" ||
							entityType === "rischio";
						if (!consumedByChildForm) {
							removeLocalStorageSelectedEntity();
						}
					}
				}
			}
		} catch (e) {
			if (e.code === "FASCICOLO_NOT_FOUND") {
				handleMissingFascicolo();
				return;
			}
			logApi.AddLog(e.message, e.stack, "loadDatas", "FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
			setIsDataLoaded(true);
		}
	}
	const loadUsers = async () => {
		try {
			const userResponse = await userApi.GetUsersByCompanyId()					
			if(userResponse.length > 0){
				setUtentiList(userResponse);
				return userResponse
			}
		} catch (error) {
			logApi.AddLog(`Errore durante il caricamento degli utenti dell'azienda ${aziendaId} : ${error.message}`, error.stack, "loadUser", "FascicoloAdd");
			throw new Error(`Errore durante il caricamento degli utenti dell'azienda`);
		}
	}
	const loadBaseTables = async () => {
		try {
			const baseAllResponse = await apiBaseAll.fetch({ tenantId: tenantId });
			const arrTipoAnag = baseTableApi.GetBaseTipoAnagrafica(baseAllResponse)
			setBaseTipoAnagList(arrTipoAnag);					
			const arrTipoDocs = baseTableApi.GetBaseTipoDocumento(baseAllResponse)
			setBaseTipoDocList(arrTipoDocs);			
			const arrBaseRischioGrado = await baseTableApi.GetBaseRischioGradoByTenantType(sharedDatas.getPropertyByName("userModel").tenantTipoId);					
			setBaseRischioGradoList(arrBaseRischioGrado);
			const arrTipoParts = baseTableApi.GetBaseTipoPartecipazione(baseAllResponse)
			setBaseTipoPartList(arrTipoParts);
			const arrTipoIdentItems = baseTableApi.GetBaseTipoIdent(baseAllResponse)
			setBaseTipoIdentList(arrTipoIdentItems);
			//Lookup provincia (Id -> Sigla) per valorizzare la provincia negli indirizzi delle stampe
			const provinceList = baseAllResponse?.data?.data?.base_provincia ?? [];
			baseProvinciaMapRef.current = new Map(provinceList.map((p) => [p.Id, p.Sigla]));
		} catch (error) {
			logApi.AddLog(`Errore durante il caricamento delle tabelle di base : ${error.message}`, error.stack, "loadBaseTables", "FascicoloAdd");
			throw new Error(`Errore durante il caricamento delle tabelle di base`);
		}
	}
	const processFascicoloData = async (fascicolo,users) => {
		try {
			const fascModel = createFascicoloModel(fascicolo,!fascicolo.stato,fascicolo.stato,fascicolo.anagrafica_fascicoli,fascicolo.prestazioni)										
			setSelFascicolo(fascModel);
			setSelFascicoloOriginal(fascicolo);
			setChbVal({ isPrivate: fascicolo.isPrivate });

			const prestByFascicolo = await prestazioneApi.GetPrestazioniByFascicoloId(fascModel.id);					
			let professionisti = [];
			//Aggiungo i professionisti che hanno effettuato una prestazione
			for ( let i = 0; i < prestByFascicolo.length; i++) {
				const prestUsersList = prestByFascicolo[i].prestazione_users || [];
				prestUsersList.forEach((prestUser) => {
					if(professionisti.filter((x) => x.userId === prestUser.UserId).length <= 0){
						professionisti.push(users.filter((x) => x.userId === prestUser.UserId)[0])
					}
				})							
			}
			setFilteredUtentiList(professionisti);
			setActiveProfIds(getProfIdsWithActivePrestazioni(prestByFascicolo));

			let isCessato = isFascicoloCessato(prestByFascicolo);

			if (isCessato) {
				setFascicoloCessato(isCessato);
			} else {
				setStatusFascicolo(fascModel.status);
				setStatusFascicoloReasons(fascModel.statusReasons);
			}
		} catch (error) {
			logApi.AddLog(`Errore durante il caricamento del fascicolo ${fascicolo.id} : ${error.message}`, error.stack, "loadFascicolo", "FascicoloAdd");
			throw new Error(`Errore durante il caricamento del fascicolo`);
		}
	}
	const loadAllegati = async (itemId) => {
		await signatureApi.PollSessionStatus(tenantId,aziendaId);
		let allegatiFromApi = await allegatiApi.GetAllegati(itemId,tenantId,aziendaId);
		setCurrentAllegati(allegatiFromApi);		
		const unavailable = allegatiFromApi.some((item) => item.id <= 233);
		setHasUnavailableAllegati(unavailable);
	};
	const handleRefreshAllegati = async () => {
		if (!itemId) return;
		await loadAllegati(itemId);
	};
	const loadSos = async (fascicoloId) => {
		try {
			let sosResponse = await sosApi.GetSOSByFascicolo(fascicoloId);
			if(sosResponse.length > 0){
				setCurrentSOSList(sosResponse);
			}
			return sosResponse;
		} catch (error) {
			logApi.AddLog(`Errore durante il caricamento delle SOS del fascicolo ${fascicoloId} : ${error.message}`, error.stack, "loadSos", "FascicoloAdd");
			throw new Error(`Errore durante il caricamento delle SOS del fascicolo`);
		}
	}
	//Home Button
	const handleGoHome = () => {
		setLocalStorageSelectedFascicolo(0);
		navigate(basePath);
	};
	//Tabs
	const [selectedTab, setSelectedTab] = useState("tabAnagrafica");
	const handleChangeTab = (event, newValue) => {
		setSelPrestazione(new prestazioneModel(0));
		setSelectedTab(newValue);
	};
	//#region Drawer
	const [openDrawer, setOpenDrawer] = useState(false);
	const [drawerTabValue, setDrawerTabValue] = useState("bancaDati");
	const [drawerHeaderTitle, setDrawerHeaderTitle] = useState("Banca dati");
	const [drawerHeaderDesc, setDrawerHeaderDesc] = useState("Verifica anagrafica in Banche dati");
	const handleDrawer = () => {
		if (openDrawer) {
			setSelBancaDatiItem(new bancaDatiModel(0, "", "", "", "", "", null, ""));
			setBancaDatiResult((prevState) => ({
				...prevState,
				Pep_Regionali: false,
				Pep_Metropolitani: false,
				Pep_Provinciali: false,
				Pep_Comunali: false,
				Pep_MaggiorOrgano: false,
				Pep_OrganiStraordinari: false,
				Pep_SindaciInCarica: false,
				Pep_Comune: false,
				UNSC: false,
				OFAC: false,
				UE: false,
				Pep_Provincia: false,
			}));
			setSelBancaDatiItemValues([]);
			setSelBancaDatiItemKeys(null);
			setBancaDatiAbbResultListNeg((prevState) => ({
				...prevState,
				BLP: [],
				DEB: [],
				DIB: [],
			}));
			setBancaDatiAbbResultListPar((prevState) => ({
				...prevState,
				PAR1: [],
				PAR2: [],
			}));
			setBancaDatiAbbResultListStr((prevState) => ({
				...prevState,
				CAR: [],
				SOC: [],
				SIN: [],
			}));
			setShowModalDetail(false);
			setBancaDatiTabValue("tabBdGratuite");
		}
		setOpenDrawer(!openDrawer);
	};
	const reloadDrawerData = async (tabValue) => {
		if (tabValue === "allegati") {
			await loadAllegati(itemId);
		} else if (tabValue === "sos") {
			try {
				const sos = await sosApi.GetSOSByFascicolo(itemId);
				if(sos.length > 0){		
					setCurrentSOSList(sos);
				}
			} catch (e) {
				logApi.AddLog(`Errore durante il caricamento delle SOS del fascicolo ${itemId} : ${e.message}`, e.stack, "reloadDrawerData/sos", "FascicoloAdd");
			}
		} else if (tabValue === "documenti") {
			// Reset delle selezioni così i dati vengono ricaricati freschi
			setSelectedPrint("");
			setSelectedUsers([]);
			setSelectedPrestToPrint([]);
			setSelectedRischiToPrint([]);
			setSelectedIndAnomaliaToPrint([]);
			setSelectedSOSToPrint(null);
			setPrestazioniList([]);
			setRischiList([]);
			setIndAnomaliaList([]);
		}
	};
	const handleDrawerTabChange = async (event, newValue) => {
		setDrawerTabValue(newValue);
		if (newValue === "bancaDati") {
			setDrawerHeaderTitle("Banca dati");
			setDrawerHeaderDesc("Verifica anagrafica in Banche dati");
		} else if (newValue === "documenti") {
			setDrawerHeaderTitle("Stampa");
			setDrawerHeaderDesc("Stampa documenti");
		} else if (newValue === "allegati") {
			setDrawerHeaderTitle(`Allegati - ${selFascicolo?.name || ""}`);
			setDrawerHeaderDesc("Gestisci gli allegati associati al fascicolo");
		} else if (newValue === "sos") {
			setDrawerHeaderTitle("SOS");
			setDrawerHeaderDesc("Segnalazioni di Operazioni Sospette");
		}
		await reloadDrawerData(newValue);
	};
	const openDrawerAction = async (newValue) => {
		setDrawerTabValue(newValue);
		setOpenDrawer(true);
		if (newValue === "bancaDati") {
			setDrawerHeaderTitle("Banca dati");
			setDrawerHeaderDesc("Verifica anagrafica in Banche dati");
		} else if (newValue === "documenti") {
			setDrawerHeaderTitle("Stampa");
			setDrawerHeaderDesc("Stampa documenti");
		} else if (newValue === "allegati") {
			setDrawerHeaderTitle(`Allegati - ${selFascicolo?.name || ""}`);
			setDrawerHeaderDesc("Gestisci gli allegati associati al fascicolo");
		} else if (newValue === "sos") {
			setDrawerHeaderTitle("SOS");
			setDrawerHeaderDesc("Segnalazioni di Operazioni Sospette");
		}
		await reloadDrawerData(newValue);
	};
	//#endregion
	//Status fascicolo
	const [statusFascicolo, setStatusFascicolo] = useState(false);
	const [statusFascicoloReasons, setStatusFascicoloReasons] = useState("");
	const [fascicoloCessato, setFascicoloCessato] = useState(false);
	//#region Banca dati
	const [showModalDetail, setShowModalDetail] = useState(false);
	const [selBancaDatiItem, setSelBancaDatiItem] = useState(new bancaDatiModel(0, "", "", "", "", "", null, ""));
	const [selBancaDatiItemKeys, setSelBancaDatiItemKeys] = useState([]);
	const [selBancaDatiItemValues, setSelBancaDatiItemValues] = useState(null);
	const [bancaDatiResultItems, setBancaDatiResultItems] = useState([]);
	//Banche dati pubbliche
	const handleCheckBancaDatiClick = async () => {
		try {
			setBackDropInternalOpen(true);
			if (
				currentAnagrafica.isPg &&
				currentAnagrafica.cf !== undefined &&
				currentAnagrafica.cf !== "" &&
				currentAnagrafica.denominazione !== undefined &&
				currentAnagrafica.denominazione !== ""
			) {
				const bancaDatiResponse = await apiSearchLegalEntity.fetch({
					identifier: currentAnagrafica.cf,
					companyName: currentAnagrafica.denominazione,
				});
				if (bancaDatiResponse.data.errors !== undefined) {
					enqueueSnackbar(t("error.generale"), { variant: "error" });
				} else {
					const bancaDatiResultArr = Object.assign([], bancaDatiResultItems);
					const arrItems = [];
					if ( bancaDatiResponse.data.data.searchLegalEntity.data.bancadati.length > 0) {
						const anagBancaDatiResultArr = Object.assign([],anagraficheBancaDatiList);
						for (var i = 0; i < bancaDatiResponse.data.data.searchLegalEntity.data.bancadati.length; i++) {
							const item = bancaDatiResponse.data.data.searchLegalEntity.data.bancadati[i];
							bancaDatiResultArr.push(new bancaDatiModel(
								0,
								item.repository,
								"",
								"",
								"",
								"",
								null,
								item.infos,
							));
							arrItems.push(new bancaDatiModel(
								0,
								item.repository,
								"",
								"",
								"",
								"",
								null,
								item.infos,
							));
							if (item.repository === "United Nations Security Council Consolidated List") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									UNSC: true,
								}));
							} else if (item.repository === "OFAC Sanctions List") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									OFAC: true,
								}));
							} else if (item.repository === "UE Sanctions List") {
								setBancaDatiResult((prevState) => ({ ...prevState, UE: true }));
							}
							//Salvo il risultato nella banca dati dell'anagrafica
							if (currentAnagrafica.id > 0) {
								await apiAddAnagraficaBancaDati.fetch({
									anagraficaId: currentAnagrafica.id,
									repository: item.repository,
									infos: JSON.stringify(item.infos),
								});
								anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
									0,
									currentAnagrafica.id,
									item.repository,
									JSON.stringify(item.infos),
									new Date(),
									loggedUserId,
								));
							}
						}
						setBancaDatiResultItems(bancaDatiResultArr);
						setAnagraficheBancaDatiList(anagBancaDatiResultArr);
						//Invio una notifica tramite pub/sub interno
						postal.publish({
							channel: "bancaDati",
							topic: "bancaDati.result",
							data: {
								result: arrItems,
							},
						});
					} else {
						//Aggiungo comunque il risultato vuoto all'elenco, così da dimostrare di aver proceduto al controllo in banca dati
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"United Nations Security Council Consolidated List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"OFAC Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"UE Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"United Nations Security Council Consolidated List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"OFAC Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"UE Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"UE Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						setBancaDatiResult((prevState) => ({
							...prevState,
							UNSC: true,
							OFAC: true,
							UE: true,
						}));
						//Salvo il risultato nella banca dati dell'anagrafica
						if (currentAnagrafica.id > 0) {
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "United Nations Security Council Consolidated List",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "OFAC Sanctions List",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "UE Sanctions List",
								infos: "",
							});
						}
						setBancaDatiResultItems(bancaDatiResultArr);
						const anagBancaDatiResultArr = Object.assign([],anagraficheBancaDatiList);
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"United Nations Security Council Consolidated List",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"OFAC Sanctions List",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"UE Sanctions List",
							"",
							new Date(),
							loggedUserId,
						));
						setAnagraficheBancaDatiList(anagBancaDatiResultArr);
						//Invio una notifica tramite pub/sub interno
						postal.publish({
							channel: "bancaDati",
							topic: "bancaDati.result",
							data: {
								result: arrItems,
							},
						});
						enqueueSnackbar(t("message.noMatch"), { variant: "success" });
					}
					//Aggiorno la data di ultima modifica del fascicolo
					selFascicolo.aziendaId = aziendaId;
					selFascicolo.tenantId = tenantId;
					let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
						selFascicolo,
						selFascicolo.isPrivate,
					);
					if (!updatedFascicolo) {
						enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
						return;
					}
					setBancaDatiTabValue("tabBdResoconto");
				}
			} else if (
				!currentAnagrafica.isPg &&
				currentAnagrafica.cf !== undefined &&
				currentAnagrafica.cf !== "" &&
				currentAnagrafica.nome !== undefined &&
				currentAnagrafica.nome !== "" &&
				currentAnagrafica.cognome !== undefined &&
				currentAnagrafica.cognome !== "" &&
				currentAnagrafica.nascitaData !== undefined &&
				currentAnagrafica.nascitaData !== ""
			) {
				const bancaDatiResponse = await apiSearchNaturalPerson.fetch({
					bornDate: currentAnagrafica.nascitaData.split("T")[0],
					name: currentAnagrafica.nome,
					surname: currentAnagrafica.cognome,
				});
				if (bancaDatiResponse.data.errors !== undefined) {
					enqueueSnackbar(t("error.generale"), { variant: "error" });
				} else {
					const bancaDatiResultArr = Object.assign([], bancaDatiResultItems);
					const arrItems = [];
					if (bancaDatiResponse.data.data.searchNaturalPerson.data.bancadati.length > 0) {
						const anagBancaDatiResultArr = Object.assign([],anagraficheBancaDatiList);
						for (var j = 0; j < bancaDatiResponse.data.data.searchNaturalPerson.data.bancadati.length; j++) {
							const item = bancaDatiResponse.data.data.searchNaturalPerson.data.bancadati[j];
							bancaDatiResultArr.push(new bancaDatiModel(
								0,
								item.repository,
								"",
								"",
								"",
								"",
								null,
								item.infos,
							));
							arrItems.push(new bancaDatiModel(
								0,
								item.repository,
								"",
								"",
								"",
								"",
								null,
								item.infos,
							));
							if (item.repository === "PEP - Amministratori Regionali") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Regionali: true,
								}));
							} else if (item.repository === "PEP - Amministratori Metropolitani") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Metropolitani: true,
								}));
							} else if (item.repository === "PEP - Amministratori Provinciali") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Provinciali: true,
								}));
							} else if (item.repository === "PEP - Amministratori Comunali") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Comunali: true,
								}));
							} else if (item.repository === "PEP - Maggior Organo") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_MaggiorOrgano: true,
								}));
							} else if (item.repository === "PEP - Organi Straordinari in carica") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_OrganiStraordinari: true,
								}));
							} else if (item.repository === "PEP - Sindaci in carica") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_SindaciInCarica: true,
								}));
							} else if (item.repository === "PEP - Comune") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Comune: true,
								}));
							} else if (item.repository.includes("PEP - Provincia (")) {
								setBancaDatiResult((prevState) => ({
									...prevState,
									Pep_Provincia: true,
								}));
							} else if (item.repository === "United Nations Security Council Consolidated List") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									UNSC: true,
								}));
							} else if (item.repository === "OFAC Sanctions List") {
								setBancaDatiResult((prevState) => ({
									...prevState,
									OFAC: true,
								}));
							} else if (item.repository === "UE Sanctions List") {
								setBancaDatiResult((prevState) => ({ ...prevState, UE: true }));
							}
							//Salvo il risultato nella banca dati dell'anagrafica
							if (currentAnagrafica.id > 0) {
								const bancaDatiAnagResponse =
									await apiAddAnagraficaBancaDati.fetch({
										anagraficaId: currentAnagrafica.id,
										repository: item.repository,
										infos: JSON.stringify(item.infos),
									});
								if (bancaDatiResponse.data.errors !== undefined) {
								} else {
									const result = bancaDatiAnagResponse.data.data.insert_anagrafica_bancadati_one;
									anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
										result.Id,
										currentAnagrafica.id,
										result.Repository,
										result.Infos,
										result.CreatedOn,
										result.CreatedBy,
									));
								}
							}
						}
						setBancaDatiResultItems(bancaDatiResultArr);
						setAnagraficheBancaDatiList(anagBancaDatiResultArr);
						//Invio una notifica tramite pub/sub interno
						postal.publish({
							channel: "bancaDati",
							topic: "bancaDati.result",
							data: {
								result: arrItems,
							},
						});
					} else {
						//Aggiungo comunque il risultato vuoto all'elenco, così da dimostrare di aver proceduto al controllo in banca dati
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Regionali",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Metropolitani",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Provinciali",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Provinciali",
							"",
							"",
							"",
							"",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Maggior Organo",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Organi Straordinari in carica",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"PEP - Sindaci in carica",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(0, "PEP - Comune", "", "", "", "", null, ""));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"United Nations Security Council Consolidated List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"OFAC Sanctions List",
							"",
							"",
							"",
							"",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						bancaDatiResultArr.push(new bancaDatiModel(
							0,
							"UE Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Regionali",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Metropolitani",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Metropolitani",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Provinciali",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Amministratori Comunali",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Maggior Organo",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Organi Straordinari in carica",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"PEP - Sindaci in carica",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(0, "PEP - Comune", "", "", "", "", null, ""));
						arrItems.push(new bancaDatiModel(
							0,
							"United Nations Security Council Consolidated List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"OFAC Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						arrItems.push(new bancaDatiModel(
							0,
							"UE Sanctions List",
							"",
							"",
							"",
							"",
							null,
							"",
						));
						setBancaDatiResult((prevState) => ({
							...prevState,
							Pep_Regionali: true,
							Pep_Metropolitani: true,
							Pep_Provinciali: true,
							Pep_Comunali: true,
							Pep_MaggiorOrgano: true,
							Pep_OrganiStraordinari: true,
							Pep_SindaciInCarica: true,
							Pep_Comune: true,
							UNSC: true,
							OFAC: true,
							UE: true,
						}));
						//Salvo il risultato nella banca dati dell'anagrafica
						if (currentAnagrafica.id > 0) {
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Amministratori Regionali",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Amministratori Metropolitani",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Amministratori Provinciali",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Amministratori Comunali",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Maggior Organo",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Organi Straordinari in carica",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Sindaci in carica",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "PEP - Comune",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "United Nations Security Council Consolidated List",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "OFAC Sanctions List",
								infos: "",
							});
							await apiAddAnagraficaBancaDati.fetch({
								anagraficaId: currentAnagrafica.id,
								repository: "UE Sanctions List",
								infos: "",
							});
						}
						setBancaDatiResultItems(bancaDatiResultArr);
						const anagBancaDatiResultArr = Object.assign([],anagraficheBancaDatiList);
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Amministratori Regionali",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Amministratori Metropolitani",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Amministratori Provinciali",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Amministratori Comunali",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Maggior Organo",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Organi Straordinari in carica",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Sindaci in carica",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"PEP - Comune",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"United Nations Security Council Consolidated List",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"OFAC Sanctions List",
							"",
							new Date(),
							loggedUserId,
						));
						anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
							0,
							currentAnagrafica.id,
							"UE Sanctions List",
							"",
							new Date(),
							loggedUserId,
						));
						setAnagraficheBancaDatiList(anagBancaDatiResultArr);
						//Invio una notifica tramite pub/sub interno
						postal.publish({
							channel: "bancaDati",
							topic: "bancaDati.result",
							data: {
								result: arrItems,
							},
						});
						enqueueSnackbar(t("message.noMatch"), { variant: "success" });
					}
					//Aggiorno la data di ultima modifica del fascicolo
					selFascicolo.aziendaId = aziendaId;
					selFascicolo.tenantId = tenantId;
					let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
						selFascicolo,
						selFascicolo.isPrivate,
					);
					if (!updatedFascicolo) {
						enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
						return;
					}
					setBancaDatiTabValue("tabBdResoconto");
				}
			}
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"handleCheckBancaDatiClick","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
			await handleReloadStatusFascicolo();
		}
	};
	//Banche dati Mode Finance
	const handleBancaDatiBuyProduct = async (item) => {
		try {
			setBackDropInternalOpen(true);
			//Acquisto il prodotto
			let modeFinResponse = null;
			try {
				modeFinResponse = await tsApiModeFinanceBuyProduct.fetch({
					url:
						"aml:mode-finance:api://companies/fiscal_code/" +
						currentAnagrafica.cf +
						"/products/" +
						item.product +
						"?item_id=" +
						tenantId,
					headers: {
						Authorization: "Bearer " + accessToken,
						"X-App-Name": "TS420",
						"X-App-Version": "1.0.0",
						"X-Request-ID": uuidv4(),
						"X-Correlation-ID": uuidv4(),
						"Content-Type": "application/json",
						"X-Item-ID": tenantId,
						"X-User-ID": loggedUserId,
					},
				});
			} catch (e) {
				logApi.AddLog(e.message,e.stack,"handleBancaDatiBuyProduct/tsApiModeFinanceBuyProduct","FascicoloAdd");
				enqueueSnackbar("Nessun documento trovato per il codice fiscale " + currentAnagrafica.cf,{ variant: "error" });
			}
			if (modeFinResponse !== null) {
				const orderId = modeFinResponse.data.order_id;
				const status = modeFinResponse.data.status;
				setBancaDatiAbbProductBought(orderId !== "" && status === "COMPLETE");
				setBancaDatiCredits(bancaDatiAbbCredits - item.price);
				//Recupero il prodotto appena acquistato
				const modeFinDocResponse = await tsApiModeFinanceGetDocument.fetch({
					url:
						"aml:mode-finance:api://orders/" +
						orderId +
						"/documents/format/JSON?item_id=" +
						tenantId,
					headers: {
						Authorization: "Bearer " + accessToken,
						"X-App-Name": "TS420",
						"X-App-Version": "1.0.0",
						"X-Request-ID": uuidv4(),
						"X-Correlation-ID": uuidv4(),
						"Content-Type": "application/json",
						"X-Item-ID": tenantId,
						"X-User-ID": loggedUserId,
					},
				});
				const result = modeFinDocResponse.data.result;
				if (item.product === "NEGATIVITA") {
					setBancaDatiResult((prevState) => ({
						...prevState,
						Negativita: true,
					}));
				} else if (item.product === "PARTECIPAZIONI") {
					setBancaDatiResult((prevState) => ({
						...prevState,
						Partecipazioni: true,
					}));
				} else if (item.product === "STRUTTURA_SOCIETARIA") {
					setBancaDatiResult((prevState) => ({
						...prevState,
						StrutturaSocietaria: true,
					}));
				}
				const anagBancaDatiResultArr = Object.assign([],anagraficheBancaDatiList);
				anagBancaDatiResultArr.push(new anagraficaBancaDatiModel(
					0,
					currentAnagrafica.id,
					item.product,
					JSON.stringify(result),
					new Date(),
					loggedUserId,
				));
				setAnagraficheBancaDatiList(anagBancaDatiResultArr);
				//Salvo il risultato nella banca dati dell'anagrafica
				if (currentAnagrafica.id > 0) {
					await apiAddAnagraficaBancaDati.fetch({
						anagraficaId: currentAnagrafica.id,
						repository: item.product,
						infos: JSON.stringify(result),
					});
				}
				const arrItems = [];
				arrItems.push(new anagraficaBancaDatiModel(
					0,
					currentAnagrafica.id,
					item.product,
					JSON.stringify(result),
					new Date(),
					loggedUserId,
				));
				//Invio una notifica tramite pub/sub interno
				postal.publish({
					channel: "bancaDati",
					topic: "bancaDati.result",
					data: {
						result: arrItems,
					},
				});
				//Aggiorno la data di ultima modifica del fascicolo
				selFascicolo.aziendaId = aziendaId;
				selFascicolo.tenantId = tenantId;
				let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
					selFascicolo,
					selFascicolo.isPrivate,
				);
				if (!updatedFascicolo) {
					enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
					return;
				}
				enqueueSnackbar(t("message.success"), { variant: "success" });
			}
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"handleBancaDatiBuyProduct","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
			await handleReloadStatusFascicolo();
		}
	};
	const handleDetailBancaDatiClick = async (bancaDatiResultToShow) => {
		const itemToShow = bancaDatiResultToShow;
		setSelBancaDatiItem(itemToShow);
		if (
			itemToShow.infos !== "" &&
			itemToShow.infos !== undefined &&
			itemToShow.infos !== null
		) {
			if (
				itemToShow.repository !== "NEGATIVITA" &&
				itemToShow.repository !== "PARTECIPAZIONI" &&
				itemToShow.repository !== "STRUTTURA_SOCIETARIA"
			) {
				let keys = null;
				const values = JSON.parse(itemToShow.infos);
				let valuesObj = null;
				if (values !== "") {
					valuesObj = JSON.parse(values);
					keys = Object.keys(valuesObj);
				}
				setSelBancaDatiItemValues(valuesObj);
				const arrKeys = [];
				keys.forEach((key) => {
					arrKeys.push(key);
				});
				setSelBancaDatiItemKeys(arrKeys);
				setShowModalDetail(true);
			} else if (itemToShow.repository === "NEGATIVITA") {
				const jsonRes = JSON.parse(itemToShow.infos);
				setBancaDatiAbbResultListNeg((prevState) => ({
					...prevState,
					BLP: jsonRes.bankruptcy_liquidation_proceedings,
					DEB: jsonRes.detrimental_behaviors,
					DIB: jsonRes.dishonored_bills,
				}));
				setShowModalDetail(true);
			} else if (itemToShow.repository === "PARTECIPAZIONI") {
				const jsonRes = JSON.parse(itemToShow.infos);
				setBancaDatiAbbResultListPar((prevState) => ({
					...prevState,
					PAR1: jsonRes.raw_data.partecipazioni_societa,
					PAR2: jsonRes.raw_data.tabella_partecipate_impresa_list,
				}));
				setShowModalDetail(true);
			} else if (itemToShow.repository === "STRUTTURA_SOCIETARIA") {
				const jsonRes = JSON.parse(itemToShow.infos);
				setBancaDatiAbbResultListStr((prevState) => ({
					...prevState,
					CAR: jsonRes.cariche,
					SOC: jsonRes.soci,
					SIN: jsonRes.sindaci_organi_controllo,
				}));
				setShowModalDetail(true);
			}
		}
	};
	const handleModalClose = () => {
		setSelBancaDatiItem(new bancaDatiModel(0, "", "", "", "", "", null, ""));
		setBancaDatiResult((prevState) => ({
			...prevState,
			Pep_Regionali: false,
			Pep_Metropolitani: false,
			Pep_Provinciali: false,
			Pep_Comunali: false,
			Pep_MaggiorOrgano: false,
			Pep_OrganiStraordinari: false,
			Pep_SindaciInCarica: false,
			Pep_Comune: false,
			UNSC: false,
			OFAC: false,
			UE: false,
		}));
		setSelBancaDatiItemValues([]);
		setSelBancaDatiItemKeys(null);
		setBancaDatiAbbResultListNeg((prevState) => ({
			...prevState,
			BLP: [],
			DEB: [],
			DIB: [],
		}));
		setBancaDatiAbbResultListPar((prevState) => ({
			...prevState,
			PAR1: [],
			PAR2: [],
		}));
		setBancaDatiAbbResultListStr((prevState) => ({
			...prevState,
			CAR: [],
			SOC: [],
			SIN: [],
		}));
		setShowModalDetail(false);
	};
	//#endregion
	//#region Subscribes
	//Rimango in ascolto per reperire le anagrafiche di cui visualizzare le informazioni
	// eslint-disable-next-line no-unused-vars
	const getAnagrafiche = postal.subscribe({
		channel: "fascicolo",
		topic: "anagrafica.list",
		callback: function (data, envelope) {
			const anagrafiche = data.anagrafiche;
			const bancheDati = data.bancheDati;
			setCurrentAnagraficheList(anagrafiche);
			setAnagraficheBancaDatiList(bancheDati);
		},
	});
	// eslint-disable-next-line no-unused-vars
	const getSelectedAnagrafica = postal.subscribe({
		channel: "fascicolo",
		topic: "anagrafica.detail",
		callback: function (data, envelope) {
			const anagrafica = data.anagrafica;
			setCurrentAnagrafica(anagrafica);
		},
	});
	// eslint-disable-next-line no-unused-vars
	const getNewSos = postal.subscribe({
		channel: "fascicolo",
		topic: "sos.add",
		callback: function (data, envelope) {
			setSelectedTab("tabIndAnomalia");
			setOpenDrawer(true);
			setDrawerTabValue("sos");
			setDrawerHeaderTitle("SOS");
			setDrawerHeaderDesc("Segnalazioni di Operazioni Sospette");
			setCurrentSOS(new sosModel(
				0,
				new Date(),
				data.sosDesc,
				"",
				"",
				loggedUserId,
				new Date(),
				undefined,
				loggedUserId,
			));
			setCurrentOriginalSOS(new sosModel(
				0,
				new Date(),
				data.sosDesc,
				"",
				"",
				loggedUserId,
				new Date(),
				undefined,
				loggedUserId,
			));
		}
	});
	//#endregion
	//#region Stampa documenti
	const printList = [
		{
			id: "istruttoria",
			name: "Istruttoria Cliente",
			nameAvv: "Istruttoria Cliente",
		},
		{
			id: "informativa",
			name: "Informativa Privacy",
			nameAvv: "Informativa Privacy",
		},
		{
			id: "astensione",
			name: "Dichiarazione di astensione del professionista",
			nameAvv: "Dichiarazione di astensione del professionista",
		},
		{
			id: "dicCliente",
			name: "Dichiarazione del cliente",
			nameAvv: "Dichiarazione del cliente",
		},
		{
			id: "dicProf",
			name: "Dichiarazione del professionista attestante ex art. 26 d.lgs. 231/2007",
			nameAvv:
				"Dichiarazione del professionista attestante ex art. 26 d.lgs. 231/2007",
		},
		{
			id: "valRischi",
			name: "Determinazione del rischio effettivo e della tipologia di adeguata verifica",
			nameAvv: "Determinazione del rischio",
		},
		{
			id: "indAnomalia",
			name: "Indicatori di anomalia",
			nameAvv: "Indicatori di anomalia",
		},
	];
	const [selectedPrint, setSelectedPrint] = useState("");
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [selectedPrestToPrint, setSelectedPrestToPrint] = useState([]);
	const [selectedRischiToPrint, setSelectedRischiToPrint] = useState([]);
	const [selectedIndAnomaliaToPrint, setSelectedIndAnomaliaToPrint] = useState([]);
	const [selectedSOSToPrint, setSelectedSOSToPrint] = useState(null);
	const [selectedProfToPrint, setSelectedProfToPrint] = useState({
		date: new Date(),
		name: "",
		address: "",
	});
	const [printCessato, setPrintCessato] = useState(false);
	const [printCessPrest, setPrintCessPrest] = useState(false);
	const [printDate, setPrintDate] = useState(new Date());
	// Stampe basate sulla lista prestazioni, che nasconde le cessate se il flag è spento
	const isPrestazioniBasedPrint = selectedPrint === "istruttoria" || selectedPrint === "astensione";
	const printUtentiList = useMemo(() => {
		if (!isPrestazioniBasedPrint || printCessPrest) return filteredUtentiList;
		return filteredUtentiList.filter((u) => activeProfIds.has(u.userId));
	}, [filteredUtentiList, activeProfIds, isPrestazioniBasedPrint, printCessPrest]);
	const handlePrintChange = async (event, value) => {
		const { id } = event.target;
		if (id.indexOf("ddlPrint-option") !== -1 || id === "ddlPrint") {
			setSelectedUsers([]);
			setSelectedPrestToPrint([]);
			setSelectedRischiToPrint([]);
			setSelectedIndAnomaliaToPrint([]);
			setSelectedSOSToPrint(null);
			setSelectedProfToPrint([]);
			setSelectedPrint(value.id);
		} else if (
			id.indexOf("ddlPrintSOS-option") !== -1 ||
			id === "ddlPrintSOS"
		) {
			setSelectedUsers([]);
			setSelectedPrestToPrint([]);
			setSelectedRischiToPrint([]);
			setSelectedIndAnomaliaToPrint([]);
			setSelectedSOSToPrint(null);
			setSelectedProfToPrint([]);
			setSelectedSOSToPrint(value);
		} else if (id === "txtPrintProfName") {
			selectedProfToPrint.name = event.target.value;
			setSelectedProfToPrint(selectedProfToPrint);
		} else if (id === "txtPrintProfAddress") {
			selectedProfToPrint.address = event.target.value;
			setSelectedProfToPrint(selectedProfToPrint);
		}
	};
	const handleUserChange = async (event, value) => {
		// Aggiorna sempre la lista degli utenti selezionati
		setSelectedUsers(value);
		// Ricarica la lista appropriata in base al tipo di stampa selezionato
		if (
			selectedPrint !== "informativa" &&
			selectedPrint !== "dicProf" &&
			selectedPrint !== "valRischi" &&
			selectedPrint !== "indAnomalia"
		) {
			// Per istruttoria, astensione, dicCliente: carica le prestazioni filtrate per utenti
			await loadPrestazioniToSelect(value);
		} else if (selectedPrint === "valRischi") {
			// Per valutazione rischi: carica tutti i rischi degli utenti selezionati
			await loadAllRischiToSelect(value);
		} else if (selectedPrint === "indAnomalia") {
			// Per indicatori anomalia: carica gli indicatori degli utenti selezionati
			await loadIndAnomaliaToSelect(value);
		}
	};
	const handlePrestToPrintClick = async (item) => {
		let prestazioni = Object.assign([], prestazioniList);
		let selectedPrestazioni = [];
		if (item.length > 0) {
			for (let index = 0; index < item.length; index++) {
				let prestId = item[index];
				let prestToPush = prestazioni.filter((x) => x.id === prestId)[0];
				selectedPrestazioni.push(prestToPush);
			}
		}
		setSelectedPrestToPrint(selectedPrestazioni);
		if (
			prestazioni.length > 0 &&
			selectedPrint !== "informativa" &&
			selectedPrint !== "dicProf" &&
			selectedPrint !== "astensione" &&
			selectedPrint !== "dicCliente"
		) {
			await loadRischiToSelect(prestazioni);
		} else {
			setRischiList([]);
			setSelectedRischiToPrint([]);
		}
	};
	const handleRischiToPrintClick = async (item) => {
		var rischi = Object.assign([], selectedRischiToPrint);
		if (rischi.filter((x) => x.id === item.id).length <= 0) {
			rischi.push(item);
		} else {
			const newRischi = rischi.filter((x) => x.id !== item.id);
			rischi = newRischi;
		}
		setSelectedRischiToPrint(rischi);
	};
	const handleLegalForReport = async (item) => {
		var legalToPrint = [];
		item.forEach((item) => {
			legalToPrint.push(item);
		});
		setSelectedLegal(legalToPrint);
	};
	const handleSOSToPrintClick = async (item) => {
		if (selectedSOSToPrint === null) {
			setSelectedSOSToPrint(item);
		} else {
			if (selectedSOSToPrint.id !== item.id) {
				setSelectedSOSToPrint(item);
			} else {
				setSelectedSOSToPrint(null);
			}
		}
	};
	const handleIndAnomaliaToPrintClick = async (item) => {
		let indicatori = Object.assign([], selectedIndAnomaliaToPrint);
		if (indicatori.filter((x) => x.id === item.id).length <= 0) {
			indicatori.push(item);
			setSelectedIndAnomaliaToPrint(indicatori);
		} else {
			const newIndicatori = indicatori.filter((x) => x.id !== item.id);
			indicatori = newIndicatori;
		}
		setSelectedIndAnomaliaToPrint(indicatori);
	};
	const handlePrintChangeDate = (date) => {
		const newDate = new Date(date);
		setSelectedProfToPrint((prevState) => ({ ...prevState, date: newDate }));
	};
	const handlePrintCessato = (event, value) => {
		const { id } = event.target;
		if (id === "chkIncludeCessAnag") {
			setPrintCessato(event.target.checked);
		}
		if (id === "chkIncludeCessPrest") {
			setPrintCessPrest(event.target.checked);
			if (!event.target.checked && isPrestazioniBasedPrint) {
				const activeUsers = selectedUsers.filter((u) => activeProfIds.has(u.userId));
				if (activeUsers.length !== selectedUsers.length) {
					setSelectedUsers(activeUsers);
					if (activeUsers.length === 0) {
						setSelectedPrestToPrint([]);
						setPrestazioniList([]);
					} else {
						loadPrestazioniToSelect(activeUsers);
					}
				}
			}
		}
	};
	const [prestazioniList, setPrestazioniList] = useState([]);
	const [rischiList, setRischiList] = useState([]);
	const [indAnomaliaList, setIndAnomaliaList] = useState([]);
	const loadPrestazioniToSelect = async (users) => {
		try {
			setBackDropInternalOpen(true);
			//Carico le prestazioni
			const prestFascResponse = await apiPrestazioniByFascicolo.fetch({
				fascicoloId: selFascicolo.id,
			});
			if (prestFascResponse.data.errors !== undefined) {
				enqueueSnackbar(t("error.generale"), { variant: "error" });
			} else {
				const arrItems = [];
				prestFascResponse.data.data.prestazione.forEach((item) => {
					let itemModel = new prestazioneModel(
						item.Id,
						item.TipoKey,
						item.PrestazioneId,
						item.base_tipo_prestazione.Nome,
						item.base_tipo_prestazione.Rischio,
						item.base_tipo_prestazione.Regole,
						item.StartDate,
						item.EndDate,
						item.PaeseId,
						item.ProvinciaId,
						item.Provincia,
						item.CittaId,
						item.Citta,
						item.ScopoId,
						item.NaturaId,
						item.Descrizione,
						item.IsPrivate,
						item.CreatedOn,
						item.ModifiedOn,
						[],
						[],
						item.AstensioneMotivo,
						item.IsEsoneroObbAst,
						item.base_scopo_prestazione.Value,
						item.base_natura_prestazione.Value,
						item.rischios,
						item.base_tipo_prestazione.BaseTenantTipoId,
						item.CreatedBy,
						item.ModifiedBy,
					);
					const arrItemsUsers = [];
					let includeInList = false;
					item.prestazione_users.forEach((itemU) => {
						arrItemsUsers.push(itemU);
					});
					// Verifica se almeno uno degli utenti selezionati è tra i professionisti della prestazione
					includeInList = arrItemsUsers.some((itemU) =>users.some((x) => x.userId === itemU.UserId));
					const arrItemsPagamenti = [];
					item.prestazione_pagamentos.forEach((itemP) => {
						arrItemsPagamenti.push(
							new prestazionePagamentoModel(
								itemP.Id,
								itemP.ValutaId,
								itemP.ValutaData,
								itemP.ValutaCambio,
								itemP.ValutaImporto,
								itemP.ImportoEuro,
								itemP.ModalitaPagamentoId,
								itemP.OrigineId,
								itemP.CreatedOn,
								itemP.ModifiedOn,
								"",
								itemP.base_mezzo_pagamento.Value,
								itemP.base_origine_fondi.Value,
								itemP.Data,
								itemP.CreatedBy,
								itemP.ModifiedBy,
								itemP.IsoCode,
							),
						);
					});
					itemModel.professionisti = arrItemsUsers;
					itemModel.pagamenti = arrItemsPagamenti;
					if (includeInList) {
						arrItems.push(itemModel);
					}
				});
				let filteredPrest = [];
				arrItems.forEach((item) => {
					if (item.isPrivate) {
						let profs = item.professionisti.map((prof) => prof.UserId);
						if (profs.includes(loggedUserId)) filteredPrest.push(item);
					} else {
						filteredPrest.push(item);
					}
				});
				setPrestazioniList(filteredPrest);
				await loadRischiToSelect(filteredPrest);
			}
			// Filtro le SOS
			let newSOSList = [];
			for (let i = 0; i < filteredUtentiList.length; i++) {
				const sosItems = currentSOSList.filter((x) => x.reportedByUser === filteredUtentiList[i].userId,);
				for (let m = 0; m < sosItems.length; m++) {
					newSOSList.push(sosItems[m]);
				}
			}
			setFilteredSOSList(newSOSList);
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"loadPrestazioniToSelect","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
		}
	};
	const loadRischiToSelect = async (prestazioni) => {
		try {
			setBackDropInternalOpen(true);
			//Carico i rischi per ogni prestazione selezionata
			let arrItems = [];
			for (var i = 0; i < prestazioni.length; i++) {
				const prest = prestazioni[i];
				const rischiResponse = await apiRischiByPrestazione.fetch({
					prestazioneId: prest.id,
				});
				if (rischiResponse.data.errors !== undefined) {
					enqueueSnackbar(t("error.generale"), { variant: "error" });
				} else {
					if (selectedPrint === "valRischi") {
						rischiResponse.data.data.rischio.forEach((item) => {
							let livelloS = t("valutazione.form.rischioInerenteLivello1");
							if (item.EfTotale >= 1 && item.EfTotale <= 1.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello1");
							} else if (item.EfTotale > 1.5 && item.EfTotale <= 2.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello2");
							} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello3");
							} else if (item.EfTotale > 3.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello4");
							}
							let adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
							if (item.EfTotale >= 1.0 && item.effettivo <= 2.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
							} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello2");
							} else if (item.effettivo > 3.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello3");
							}
							let itemModel = new rischioModel(
								item.Id,
								new Date(item.Date).toLocaleDateString(),
								prest,
								item.SpA1Livello,
								item.SpA1_1,
								item.SpA1_2,
								item.SpA1_3,
								item.SpA1_4,
								item.SpA1_5,
								item.SpA1_6,
								item.SpA1_7,
								item.SpA1Altro,
								item.SpA1AltroNote,
								item.SpA2Livello,
								item.SpA2_1,
								item.SpA2_2,
								item.SpA2_3,
								item.SpA2Altro,
								item.SpA2AltroNote,
								item.SpA3Livello,
								item.SpA3_1,
								item.SpA3_2,
								item.SpA3_3,
								item.SpA3_4,
								item.SpA3Altro,
								item.SpA3AltroNote,
								item.SpA4Livello,
								item.SpA4_1,
								item.SpA4_2,
								item.SpA4_3,
								item.SpA4_4,
								item.SpA4Altro,
								item.SpA4AltroNote,
								item.SpASubTotale,
								item.SpB1Livello,
								item.SpB1_1,
								item.SpB1_2,
								item.SpB1_3,
								item.SpB1_4,
								item.SpB1Altro,
								item.SpB1AltroNote,
								item.SpB2Livello,
								item.SpB2_1,
								item.SpB2_2,
								item.SpB2_3,
								item.SpB2_4,
								item.SpB2Altro,
								item.SpB2AltroNote,
								item.SpB3Livello,
								item.SpB3_1,
								item.SpB3_2,
								item.SpB3_3,
								item.SpB3Altro,
								item.SpB3AltroNote,
								item.SpB4Livello,
								item.SpB4_1,
								item.SpB4_2,
								item.SpB4_4,
								item.SpB4Altro,
								item.SpB4AltroNote,
								item.SpB5Livello,
								item.SpB5_1,
								item.SpB5_2,
								item.SpB5_3,
								item.SpB5Altro,
								item.SpB5AltroNote,
								item.SpB6Livello,
								item.SpB6_1,
								item.SpB6_2,
								item.SpB6_3,
								item.SpB6_4,
								item.SpB6Altro,
								item.SpB6AltroNote,
								item.SpB7Livelllo,
								item.SpBSubTotale,
								item.SpC1_1_Livello,
								item.SpC1_2_Livello,
								item.SpC1_3_Livello,
								item.SpCSubTotale,
								item.EfRiTotale,
								item.EfSpTotale,
								item.EfTotale,
								item.EfAdeguataLivello,
								item.HasTabellaB,
								item.ProvidedBy,
								item.CreatedOn,
								item.ModifiedOn,
								"",
								livelloS,
								adeguataVerificaS,
								new Date(item.DataControlloCostante).toLocaleDateString(),
								item.CreatedBy,
								item.ModifiedBy,
								new Date(prest.dataEnd).toLocaleDateString(),
							);
							if (selectedUsers.filter((x) => x.userId === item.ProvidedBy).length > 0) {
								arrItems.push(itemModel);
							}
						});
						//Se c'è un solo record lo seleziono di default
						if (arrItems.length === 1) {
							var rischi = Object.assign([], selectedRischiToPrint);
							rischi = arrItems;
							setSelectedRischiToPrint(rischi);
						}
					} else {
						if (rischiResponse.data.data.rischio.length > 0) {
							const item = rischiResponse.data.data.rischio[0];
							let livelloS = t("valutazione.form.rischioInerenteLivello1");
							if (item.EfTotale >= 1 && item.EfTotale <= 1.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello1");
							} else if (item.EfTotale > 1.5 && item.EfTotale <= 2.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello2");
							} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello3");
							} else if (item.EfTotale > 3.5) {
								livelloS = t("valutazione.form.rischioInerenteLivello4");
							}
							let adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
							if (item.EfTotale >= 1.0 && item.effettivo <= 2.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
							} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello2");
							} else if (item.effettivo > 3.5) {
								adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello3");
							}
							arrItems.push(new rischioModel(
								item.Id,
								new Date(item.Date).toLocaleDateString(),
								prest,
								item.SpA1Livello,
								item.SpA1_1,
								item.SpA1_2,
								item.SpA1_3,
								item.SpA1_4,
								item.SpA1_5,
								item.SpA1_6,
								item.SpA1_7,
								item.SpA1Altro,
								item.SpA1AltroNote,
								item.SpA2Livello,
								item.SpA2_1,
								item.SpA2_2,
								item.SpA2_3,
								item.SpA2Altro,
								item.SpA2AltroNote,
								item.SpA3Livello,
								item.SpA3_1,
								item.SpA3_2,
								item.SpA3_3,
								item.SpA3_4,
								item.SpA3Altro,
								item.SpA3AltroNote,
								item.SpA4Livello,
								item.SpA4_1,
								item.SpA4_2,
								item.SpA4_3,
								item.SpA4_4,
								item.SpA4Altro,
								item.SpA4AltroNote,
								item.SpASubTotale,
								item.SpB1Livello,
								item.SpB1_1,
								item.SpB1_2,
								item.SpB1_3,
								item.SpB1_4,
								item.SpB1Altro,
								item.SpB1AltroNote,
								item.SpB2Livello,
								item.SpB2_1,
								item.SpB2_2,
								item.SpB2_3,
								item.SpB2_4,
								item.SpB2Altro,
								item.SpB2AltroNote,
								item.SpB3Livello,
								item.SpB3_1,
								item.SpB3_2,
								item.SpB3_3,
								item.SpB3Altro,
								item.SpB3AltroNote,
								item.SpB4Livello,
								item.SpB4_1,
								item.SpB4_2,
								item.SpB4_4,
								item.SpB4Altro,
								item.SpB4AltroNote,
								item.SpB5Livello,
								item.SpB5_1,
								item.SpB5_2,
								item.SpB5_3,
								item.SpB5Altro,
								item.SpB5AltroNote,
								item.SpB6Livello,
								item.SpB6_1,
								item.SpB6_2,
								item.SpB6_3,
								item.SpB6_4,
								item.SpB6Altro,
								item.SpB6AltroNote,
								item.SpB7Livelllo,
								item.SpBSubTotale,
								item.SpC1_1_Livello,
								item.SpC1_2_Livello,
								item.SpC1_3_Livello,
								item.SpCSubTotale,
								item.EfRiTotale,
								item.EfSpTotale,
								item.EfTotale,
								item.EfAdeguataLivello,
								item.HasTabellaB,
								item.ProvidedBy,
								item.CreatedOn,
								item.ModifiedOn,
								"",
								livelloS,
								adeguataVerificaS,
								new Date(item.DataControlloCostante).toLocaleDateString(),
								item.CreatedBy,
								item.ModifiedBy,
								new Date(prest.dataEnd).toLocaleDateString(),
							));
						}
					}
				}
			}
			setRischiList(arrItems);
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "loadRischiToSelect", "FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
		}
	};
	const loadAllRischiToSelect = async (users) => {
		try {
			setBackDropInternalOpen(true);
			const rischiResponse = await apiRischiByTenant.fetch({
				tenantId: tenantId,
				aziendaId: aziendaId,
				userId: loggedUserId,
			});
			if (rischiResponse.data.errors !== undefined) {
				enqueueSnackbar(t("error.generale"), { variant: "error" });
			} else {
				const arrItems = [];
				const filteredRischi = rischiResponse.data.data.rischio.filter(
					(x) => x.prestazione.fascicolo.Id === selFascicolo.id,
				);
				for (let i = 0; i < users.length; i++) {
					const userItem = users[i];
					const items = filteredRischi.filter((x) => x.ProvidedBy === userItem.userId).length > 0
							? filteredRischi.filter((x) => x.ProvidedBy === userItem.userId)
							: null;
					if (items) {
						for (let i = 0; i < items.length; i++) {
							const item = items[i];
							if (item !== null &&arrItems.filter((x) => x.id === item.Id).length <= 0) {
								let prest = {
									prestazioneNome: item.prestazione.base_tipo_prestazione.Nome,
									baseTenantTipoId:
										item.prestazione.base_tipo_prestazione.BaseTenantTipoId,
								};
								let livelloS = t("valutazione.form.rischioInerenteLivello1");
								if (item.EfTotale >= 1 && item.EfTotale <= 1.5) {
									livelloS = t("valutazione.form.rischioInerenteLivello1");
								} else if (item.EfTotale > 1.5 && item.EfTotale <= 2.5) {
									livelloS = t("valutazione.form.rischioInerenteLivello2");
								} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
									livelloS = t("valutazione.form.rischioInerenteLivello3");
								} else if (item.EfTotale > 3.5) {
									livelloS = t("valutazione.form.rischioInerenteLivello4");
								}
								let adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
								if (item.EfTotale >= 1.0 && item.effettivo <= 2.5) {
									adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello1");
								} else if (item.EfTotale > 2.5 && item.EfTotale <= 3.5) {
									adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello2");
								} else if (item.effettivo > 3.5) {
									adeguataVerificaS = t("valutazione.form.adeguataVerificaLivello3");
								}
								let livelloAvv = getLivelloAvv(item.SpASubTotale + item.SpBSubTotale);
								let adeguataAvv = getAdeguataAvv(item.SpASubTotale + item.SpBSubTotale);
								let itemModel = new rischioModel(
									item.Id,
									new Date(item.Date).toLocaleDateString(),
									prest,
									item.SpA1Livello,
									item.SpA1_1,
									item.SpA1_2,
									item.SpA1_3,
									item.SpA1_4,
									item.SpA1_5,
									item.SpA1_6,
									item.SpA1_7,
									item.SpA1Altro,
									item.SpA1AltroNote,
									item.SpA2Livello,
									item.SpA2_1,
									item.SpA2_2,
									item.SpA2_3,
									item.SpA2Altro,
									item.SpA2AltroNote,
									item.SpA3Livello,
									item.SpA3_1,
									item.SpA3_2,
									item.SpA3_3,
									item.SpA3_4,
									item.SpA3Altro,
									item.SpA3AltroNote,
									item.SpA4Livello,
									item.SpA4_1,
									item.SpA4_2,
									item.SpA4_3,
									item.SpA4_4,
									item.SpA4Altro,
									item.SpA4AltroNote,
									item.SpASubTotale,
									item.SpB1Livello,
									item.SpB1_1,
									item.SpB1_2,
									item.SpB1_3,
									item.SpB1_4,
									item.SpB1Altro,
									item.SpB1AltroNote,
									item.SpB2Livello,
									item.SpB2_1,
									item.SpB2_2,
									item.SpB2_3,
									item.SpB2_4,
									item.SpB2Altro,
									item.SpB2AltroNote,
									item.SpB3Livello,
									item.SpB3_1,
									item.SpB3_2,
									item.SpB3_3,
									item.SpB3Altro,
									item.SpB3AltroNote,
									item.SpB4Livello,
									item.SpB4_1,
									item.SpB4_2,
									item.SpB4_4,
									item.SpB4Altro,
									item.SpB4AltroNote,
									item.SpB5Livello,
									item.SpB5_1,
									item.SpB5_2,
									item.SpB5_3,
									item.SpB5Altro,
									item.SpB5AltroNote,
									item.SpB6Livello,
									item.SpB6_1,
									item.SpB6_2,
									item.SpB6_3,
									item.SpB6_4,
									item.SpB6Altro,
									item.SpB6AltroNote,
									item.SpB7Livello,
									item.SpBSubTotale,
									item.SpC1_1_Livello,
									item.SpC1_2_Livello,
									item.SpC1_3_Livello,
									item.SpCSubTotale,
									item.EfRiTotale,
									item.EfSpTotale,
									item.EfTotale,
									item.EfAdeguataLivello,
									item.HasTabellaB,
									item.ProvidedBy,
									item.CreatedOn,
									item.ModifiedOn,
									"",
									userItem.tenantTipoId === 3 ? livelloAvv : livelloS,
									userItem.tenantTipoId === 3 ? adeguataAvv : adeguataVerificaS,
									new Date(item.DataControlloCostante).toLocaleDateString(),
									item.CreatedBy,
									item.ModifiedBy,
									new Date(item.prestazione.EndDate).toLocaleDateString(),
								);
								arrItems.push(itemModel);
							}
						}
					}
				}
				setRischiList(arrItems);
			}
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"loadAllRischiToSelect","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
		}
	};
	const getLivelloAvv = (livello) => {
		if (livello >= 10 && livello <= 20) {
			return "Rischio irrilevante";
		} else if (livello > 20 && livello <= 30) {
			return "Rischio basso";
		} else if (livello > 30 && livello <= 35) {
			return "Rischio medio/moderato";
		} else if (livello > 35 && livello <= 40) {
			return "Rischio moderato/alto";
		} else {
			return "Rischio elevato e palese";
		}
	};
	const getAdeguataAvv = (livello) => {
		if (livello >= 10 && livello <= 20) {
			return "Semplificata";
		} else if (livello > 20 && livello <= 30) {
			return "Semplificata";
		} else if (livello > 30 && livello <= 35) {
			return "Ordinaria";
		} else if (livello > 35 && livello <= 40) {
			return "Rafforzata";
		} else {
			return "Rafforzata";
		}
	};
	const loadIndAnomaliaToSelect = async (users) => {
		try {
			setBackDropInternalOpen(true);
			//Carico gli indicatori
			const indResponse = await apiIndicatoriByFascicolo.fetch({
				fascicoloId: selFascicolo.id,
			});
			if (indResponse.data.errors !== undefined) {
				enqueueSnackbar(t("error.generale"), { variant: "error" });
			} else {
				const arrItems = [];
				for ( let i = 0; i < indResponse.data.data.indicatore_anomalia.length; i++) {
					const item = indResponse.data.data.indicatore_anomalia[i];
					let itemModel = new indicatoreAnomaliaModel(
						item.Id,
						item.Date,
						item.ProvidedBy,
						JSON.parse(item.Result),
						item.Note,
						item.CreatedOn,
						item.ModifiedOn,
						item.CreatedBy,
						item.ModifiedBy,
					);
					let includeInList = users.filter((x) => x.userId === item.ProvidedBy).length > 0;
					if (includeInList) {
						arrItems.push(itemModel);
					}
				}
				setIndAnomaliaList(arrItems);
				setSelectedIndAnomaliaToPrint(arrItems);
			}
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"loadIndAnomaliaToSelect","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
		}
	};
	const handlePrintClick = async () => {
		try {
			setBackDropInternalOpen(true);
			var canIProceed = false;
			if (selectedPrint === "istruttoria" || selectedPrint === "astensione") {
				if (selectedPrestToPrint.length <= 0) {
					enqueueSnackbar("Selezionare almeno una prestazione per lanciare la stampa",{ variant: "error" });
					return;
				} else {
					canIProceed = true;
				}
			} else if (selectedPrint === "informativa") {
				canIProceed = true;
			} else if (selectedPrint === "dicProf") {
				canIProceed =
					selectedProfToPrint !== null &&
					selectedProfToPrint.date !== undefined &&
					selectedProfToPrint.name !== undefined &&
					selectedProfToPrint.name !== "" &&
					selectedProfToPrint.address !== undefined &&
					selectedProfToPrint.address !== "";
			} else if (selectedPrint === "valRischi") {
				if (selectedRischiToPrint.length <= 0) {
					enqueueSnackbar("Selezionare almeno una valutazione per lanciare la stampa",{ variant: "error" });
					return;
				} else {
					canIProceed = true;
				}
			} else if (selectedPrint === "indAnomalia") {
				if (selectedIndAnomaliaToPrint.length <= 0) {
					enqueueSnackbar("Selezionare almeno un indicatore di anomalia per lanciare la stampa",{ variant: "error" });
					return;
				} else {
					canIProceed = true;
				}
			} else if (selectedPrint === "dicCliente") {
				if (selectedLegal.length <= 0) {
					enqueueSnackbar("Selezionare almeno un legale rappresentante per lanciare la stampa",{ variant: "error" });
					return;
				} else {
					canIProceed = true;
				}
			}
			if (canIProceed) {
				//Recupero le info del cliente
				const anagResponse = await apiAnagraficaByFascicolo.fetch({
					fascicoloId: selFascicolo.id,
				});
				const anagrafica = anagResponse.data.data.anagrafica.length > 0
						? anagResponse.data.data.anagrafica
						: null;
				//Cliente
				const tipoAnagId1 = baseTipoAnagList.filter((x) => x.key === TIPO_ANAGRAFICA_CLIENTE_PF_KEY)[0].id;
				const tipoAnagId2 = baseTipoAnagList.filter((x) => x.key === TIPO_ANAGRAFICA_CLIENTE_PG_KEY)[0].id;
				const cliente = anagrafica.filter((x) =>
					x.IsDeleted === false &&
					x.anagrafica_fascicolos.some(
						(y) =>
							y.FascicoloId === selFascicolo.id &&
							(y.TipoAnagId === tipoAnagId1 || y.TipoAnagId === tipoAnagId2),
					)
				)[0];
				//Identificazioni: per ogni anagrafica del fascicolo (distinta per Cf) recupero la prima
				//identificazione nel tenant e l'indirizzo dello studio in cui è avvenuta				
				switch (selectedPrint) {
					case "istruttoria": {
						for (var k = 0; k < selectedUsers.length; k++) {
							const prof = selectedUsers[k];
							let allPrestIds = selectedPrestToPrint.filter((x) => x.professionisti.some(l=>l.UserId === prof.userId)).map((x) => x.id);
							let parameterPrest = "";
							allPrestIds.forEach((l) => (parameterPrest += `${l.toString()};`));
							const docResponse = await apiGetIstruttoriaCliente.fetch({
								prestazioneIdList: parameterPrest,
								userId: prof.userId,
								isToBeSaved: true,
								isAllegato: true,
								includeTerminated: printCessato,
								datePrint: printDate.toLocaleDateString(),
							});
							const istruttoriaValue = docResponse.data.data.getIstruttoriaClienteReport.response?.value ?? {};
							const istruttoriaBase64 = istruttoriaValue.fileBase64 || istruttoriaValue.base64 || istruttoriaValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.istruttoriaCliente", "Istruttoria cliente"), istruttoriaBase64);
							//Preparo i dati per la stampa della dichiarazione del professionista
							let anagraficheNonIdentificateDaErogatore = anagrafica.filter(p => p.IdentifiedByUser !== prof.userId);
							for (const anagToPrint of anagraficheNonIdentificateDaErogatore){
								let responseForPrinting = await aziendaApi.GetPrimaIdentificazioneByCf(anagToPrint.Cf, undefined, baseProvinciaMapRef.current);;
								let identName = anagToPrint?.IdentifiedByUser ? utentiList.find((x) => x.userId === anagToPrint.IdentifiedByUser)?.completeName : "";
								if (!identName) {
									const { userResponse } = await userApi.GetUserById(anagToPrint.IdentifiedByUser);
									identName = userResponse ? `${userResponse.Name} ${userResponse.Surname}` : "";
								}
								const params = {
									date: anagToPrint.IdentifiedOn,
									name: identName,
									address: responseForPrinting?.address || "",
									anagraficaId: anagToPrint.Id,
									userIdErogatore: prof.userId,
								};
								await printDicProf(params);		
							}																					
						}
						break;
					}
					case "informativa": {
						for (let j = 0; j < selectedUsers.length; j++) {
							const docResponse = await apiGetInformativaPrivacy.fetch({
								fascicoloId: selFascicolo.id,
								userId: selectedUsers[j].userId,
								isToBeSaved: true,
								isAllegato: true,
								datePrint: printDate.toLocaleDateString(),
							});
							//Apro il file nel visualizzatore PDF
							const informativaValue = docResponse.data.data.getInformativaPrivacyReport.response?.value ?? {};
							const informativaBase64 = informativaValue.fileBase64 || informativaValue.base64 || informativaValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.informativaPrivacy", "Informativa privacy"), informativaBase64);
						}
						break;
					}
					case "astensione": {
						for (var h = 0; h < selectedPrestToPrint.length; h++) {
							const prest = selectedPrestToPrint[h];
							for (var p = 0; p < prest.professionisti.length; p++) {
								const prof = prest.professionisti[p];
								if (selectedUsers.filter((x) => x.userId === prof.UserId).length >0) {
									const profItem = selectedUsers.filter((x) => x.userId === prof.UserId,)[0];
									const isSelectedSos = selectedSOSToPrint !== null && selectedSOSToPrint.id
											? selectedSOSToPrint.reportedByUser === profItem.userId
											: false;
									const docResponse = await apiGetAstensioneProf.fetch({
										prestazioneId: prest.id,
										userId: profItem.userId,
										isSelectedSos: isSelectedSos,
										selectedSosMotivo: isSelectedSos ? selectedSOSToPrint.motivo : "",
										isToBeSaved: true,
										isAllegato: true,
										datePrint: printDate.toLocaleDateString(),
									});
									//Apro il file nel visualizzatore PDF
									const astValue = docResponse.data.data.getAstensioneProfessionistaReport.response?.value ?? {};
									const astBase64 = astValue.fileBase64 || astValue.base64 || astValue.FileBase64 || null;
									handleOpenPdfViewer(t("stampe.astensioneProfessionista", "Astensione professionista"), astBase64);
								}
							}
						}
						break;
					}
					case "dicCliente": {
						for (var d = 0; d < selectedLegal.length; d++) {
							let legalRepprId = selectedLegal[d];
							const docResponse = await apiGetDichiarazioneCliente.fetch({
								fascicoloId: selFascicolo.id,
								legalReprId: legalRepprId,
								userId: loggedUserId,
								isToBeSaved: true,
								isAllegato: true,
								includeTerminated: printCessato,
								datePrint: printDate.toLocaleDateString(),
								includeTerminatedPrestazioni: printCessPrest,
							});
							//Apro il file nel visualizzatore PDF
							const dicClienteValue = docResponse.data.data.getDichiarazioneClienteReport.response?.value ?? {};
							const dicClienteBase64 = dicClienteValue.fileBase64 || dicClienteValue.base64 || dicClienteValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.dichiarazioneCliente", "Dichiarazione cliente"), dicClienteBase64);
						}
						break;
					}
					case "dicProf": {						
						for (var g = 0; g < selectedUsers.length; g++) {
							const docResponse = await apiGetDichiarazioneProfessionista.fetch(
								{
									fascicoloId: selFascicolo.id,
									nomeIdentificatore: selectedProfToPrint.name,
									indirizzoIdentificatore: selectedProfToPrint.address,
									anagraficaId: cliente.Id,
									userIdErogatore: selectedUsers[g].userId,
									isToBeSaved: true,
									isAllegato: true,
									datePrint: printDate.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }),
									dataIdentificatore: selectedProfToPrint.date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })
								},
							);
							//Apro il file nel visualizzatore PDF
							const dicProfValue = docResponse.data.data.getDichiarazioneProfessionistaReport.response?.value ?? {};
							const dicProfBase64 = dicProfValue.fileBase64 || dicProfValue.base64 || dicProfValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.dichiarazioneProfessionista", "Dichiarazione professionista"), dicProfBase64);
						}
						break;
					}
					case "valRischi": {						
						for (var v = 0; v < selectedRischiToPrint.length; v++) {
							const rischio = selectedRischiToPrint[v];
							let docResponse = await apiGetValutazioneRischio.fetch({
								rischioId: rischio.id,
								userId: rischio.providedBy,
								isToBeSaved: true,
								isAllegato: true,
								datePrint: printDate.toLocaleDateString(),
							});
							//Apro il file nel visualizzatore PDF
							const valRischioValue = docResponse.data.data.getValutazioneRischioReport.response?.value ?? {};
							const valRischioBase64 = valRischioValue.fileBase64 || valRischioValue.base64 || valRischioValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.valutazioneRischio", "Valutazione rischio"), valRischioBase64);
						}
						break;
					}
					case "indAnomalia": {						
						for (var x = 0; x < selectedIndAnomaliaToPrint.length; x++) {
							const indAnomalia = selectedIndAnomaliaToPrint[x];
							const docResponse = await apiGetIndicatoriAnomalia.fetch({
								indicatoreAnomaliaId: indAnomalia.id,
								userId: indAnomalia.providedBy,
								isAllegato: true,
								isToBeSaved: true,
								datePrint: printDate.toLocaleDateString(),
							});
							//Apro il file nel visualizzatore PDF
							const indAnomaliaValue = docResponse.data.data.getIndicatoreAnomaliaReport.response?.value ?? {};
							const indAnomaliaBase64 = indAnomaliaValue.fileBase64 || indAnomaliaValue.base64 || indAnomaliaValue.FileBase64 || null;
							handleOpenPdfViewer(t("stampe.indicatoreAnomalia", "Indicatore di anomalia"), indAnomaliaBase64);
						}
						break;
					}
					default: {
						break;
					}
				}
				setSelectedPrint("");
				setSelectedUsers([]);
				setSelectedPrestToPrint([]);
				setSelectedRischiToPrint([]);
				setSelectedIndAnomaliaToPrint([]);
				setSelectedSOSToPrint(null);
				setPrestazioniList([]);
				setRischiList([]);
				setSelectedProfToPrint({ date: new Date(), name: "", address: "" });
			} else {
				if (selectedPrint === "dicProf") {
					enqueueSnackbar(t("error.missingFields"), { variant: "error" });
				}
			}
		} catch (e) {
			let message = apiErrorHelpers.handleApiError(e);
			logApi.AddLog(e.message, e.stack, "handlePrintClick", "FascicoloAdd");
			enqueueSnackbar(message || t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
			setSelectedPrestToPrint([]);
			await loadAllegati(itemId);
		}
	};
	const printDicProf = async (params) => {
		try {
			const docResponse = await apiGetDichiarazioneProfessionista.fetch({
				fascicoloId: selFascicolo.id,
				nomeIdentificatore: params.name,
				indirizzoIdentificatore: params.address,
				anagraficaId: params.anagraficaId,
				userIdErogatore: params.userIdErogatore,
				isToBeSaved: true,
				isAllegato: true,
				datePrint: printDate.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }),
				dataIdentificatore: params.date? new Date(params.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""
			});
			//Apro il file nel visualizzatore PDF
			const printDicProfValue = docResponse.data.data.getDichiarazioneProfessionistaReport.response?.value ?? {};
			const printDicProfBase64 = printDicProfValue.fileBase64 || printDicProfValue.base64 || printDicProfValue.FileBase64 || null;
			handleOpenPdfViewer(t("stampe.dichiarazioneProfessionista", "Dichiarazione professionista"), printDicProfBase64);
		} catch (e) {
			let message = apiErrorHelpers.handleApiError(e);
			logApi.AddLog(e.message, e.stack, "printDicProf", "FascicoloAdd");
			enqueueSnackbar(message || t("error.generale"), { variant: "error" });
		}
	};
	//#endregion
	//#region Allegati
	const [allBrowserFiles, setAllBrowserFiles] = useState([]);
	const [pendingSosFiles, setPendingSosFiles] = useState([]);
	const handleUploadFile = (type = "fascicolo", sosId = null) => async (file) => {
		const canUpload = () => {
			if (!getFascicoloPermissions().canCreate) return false;
			const sosPermissions = getSOSPermissions();
			return type !== "sos" || (sosId > 0 ? sosPermissions.canUpdate : sosPermissions.canCreate);
		};
		if (!canUpload()) return false;
		setBackDropInternalOpen(true);
		try {
			if (file["length"] > 20) {
				enqueueSnackbar("Puoi selezionare fino a un massimo di 20 files formato PDF",{ variant: "warning" });
				setAllBrowserFiles([]);
				return false;
			}
			const browserFiles = Array.from(file || []);
			if (browserFiles.length === 0) {
				return false;
			}
			if (type === "sos" && (!sosId || sosId <= 0)) {
				for (let j = 0; j < browserFiles.length; j++) {
					const currFile = browserFiles[j];
					if (currFile.type !== "application/pdf") {
						enqueueSnackbar(`Il file ${currFile.name} non è un formato PDF, seleziona solo files formato PDF e riprova`,{ variant: "error" });
						return false;
					}
					if (currFile.size > 10485760) {
						enqueueSnackbar(`Il file ${currFile.name} supera la dimensione di 10MB`,{ variant: "error" });
						return false;
					}
					if (currFile.size < 4) {
						enqueueSnackbar(`Il file ${currFile.name} è malformato, impossibile effettuare il salvataggio`,{ variant: "error" });
						return false;
					}
				}
				if (pendingSosFiles.length + browserFiles.length > 20) {
					enqueueSnackbar("Puoi selezionare fino a un massimo di 20 files formato PDF",{ variant: "warning" });
					return false;
				}
				setPendingSosFiles((prev) => [...prev, ...browserFiles]);
				enqueueSnackbar("File accodati: verranno associati automaticamente al salvataggio della SOS",{ variant: "info" });
				return true;
			}
			var encodedFiles = [];
			for (var i = 0; i < file["length"]; i++) {
				const encodedFile = await fascicoloHelpers.EncodeBase64(file[i]);
				if (encodedFile && encodedFile.length > 0) {
					encodedFiles.push(encodedFile[0]);
				}
			}
			setAllBrowserFiles(browserFiles);
			const fileArr = encodedFiles;
			//Solo se è un'anagrafica esistente procedo all'inserimento degli allegati
			//altrimenti l'inserimento viene effettuato al salvataggio dell'anagrafica
			if (selFascicolo.id > 0) {
				for (let j = 0; j < fileArr.length; j++) {
					const file = browserFiles[j];
					if (file.type !== "application/pdf") {
						enqueueSnackbar(`Il file ${file.name} non è un formato PDF, seleziona solo files formato PDF e riprova`,{ variant: "error" });
						setAllBrowserFiles([]);
						return false;
					}
					if (file.size > 10485760) {
						enqueueSnackbar(`Il file ${file.name} supera la dimensione di 10MB`,{ variant: "error" });
						setAllBrowserFiles([]);
						return false;
					}
					if (file.size < 4) {
						enqueueSnackbar(`Il file ${file.name} è malformato, impossibile effettuare il salvataggio`,{ variant: "error" });
						setAllBrowserFiles([]);
						return false;
					}
				}
				for (var z = 0; z < fileArr.length; z++) {
					try {
						const file = browserFiles[z];
						const encFile = fileArr[z];
						const encFileBase64 = encFile.encrypt.substring(encFile.encrypt.indexOf("base64,"),encFile.encrypt.length).split(",")[1];							
						const fileJson = { file_base64: encFileBase64, content_type: file.type, file_name: file.name };
						if (fileJson.file_base64 && fileJson.content_type) {								
							if (!canUpload()) return false;
							let uploadedAttachment = await allegatiApi.UploadAllegato(fileJson.file_base64, fileJson.content_type,tenantId,aziendaId,type)
							if(uploadedAttachment){
								if (!canUpload()) return false;
								const response = await apiAddAllegato.fetch({
									tenantId: tenantId,
									entityType: type,
									entityId: type === "sos" ? sosId : selFascicolo.id,
									fileName: uploadedAttachment.file_name,
									fileType: file.type,
									fileUrl: uploadedAttachment.file_path,
									displayedName: fileJson.file_name,
								});
								logApi.AddLog(
									`UPLOAD FILE: ${file?.name ?? "N/A"}; ID: ${response.data?.data?.insert_allegato_one?.Id ?? "N/A"}; FascicoloID: ${selFascicolo.id}; UtenteID: ${utentiList.filter((ut) => ut.userId === loggedUserId)[0]?.email};`,
									"",
									"handleUploadFile",
									"FascicoloAdd",
									"INFO",
								);
								await loadAllegati(itemId);
							}
						} else {
							enqueueSnackbar(`Il file ${file.name} è malformato, impossibile effettuare il salvataggio`,{ variant: "error" });
							setAllBrowserFiles([]);
							return false;
						}
					} catch (e) {
						logApi.AddLog(e?.message || String(e),e?.stack || "","handleUploadFile","FascicoloAdd");
						enqueueSnackbar(t("error.generale"), { variant: "error" });
						return false;
					}
				}
				//Aggiorno la data di ultima modifica del fascicolo
				if (!canUpload()) return false;
				selFascicolo.aziendaId = aziendaId;
				selFascicolo.tenantId = tenantId;
				let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
					selFascicolo,
					selFascicolo.isPrivate,
				);
				if (!updatedFascicolo) {
					enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
					return false;
				}
				//Per gli allegati SOS il ricarico è già fatto da handleBtnAddSOSClick
				if (type !== "sos") {
					await handleReloadStatusFascicolo();
				}
				setAllBrowserFiles([]);
				return true;
			}
			return false;
		} catch (e) {
			logApi.AddLog(e?.message || String(e),e?.stack || "","handleUploadeFile","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
			return false;
		} finally {
			setBackDropInternalOpen(false);
		}
	};
	const handleDeleteAllegatoClick = (allegato) => {
		if (!getFascicoloPermissions().canDelete) return;
		setIdToPerform(allegato.id);
		setTypeToPerform("ALL");
		setShowDeleteModal(true);
	};
	const handleOpenPdfViewer = (title, base64, onDownload, signatureStatus = null) => {
		const downloadFn = onDownload || (base64 ? async () => {
			const link = document.createElement("a");
			link.href = `data:application/pdf;base64,${base64}`;
			link.download = `${title || "documento"}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} : null);
		const newDocId = `pdf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const newDoc = { id: newDocId, title: title || "", base64, onDownload: downloadFn, signatureStatus };
		setPdfDocuments(prev => [...prev, newDoc]);
		setActivePdfDocumentId(newDocId);
	};
	const handleDownloadAllegatoClick = async (allegato) => {
		if (allegato && allegato.fileUrl) {
			if (allegato.fileType === "application/pdf") {
				const b64 = await allegatiApi.DownloadAllegatoBase64(allegato.id, tenantId, aziendaId);
				if (b64) {
					handleOpenPdfViewer(
						allegato.displayedName || allegato.fileName,
						b64,
						async () => {
							const link = document.createElement("a");
							link.href = `data:application/pdf;base64,${b64}`;
							link.download = allegato.fileName || allegato.displayedName || "file.pdf";
							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						},
						allegato.signatureStatus ?? null
					);
				}
			} else {
				window.open(allegato.fileUrl, "_blank", "noopener,noreferrer");
			}
		}
	};
	//#endregion

	//#region SOS
	const handleChangeSOS = (event, value) => {
		const { id } = event.target;
		if (id.indexOf("ddlReportedBy-option") !== -1 || id === "ddlReportedBy") {
			currentSOS.reportedByUser = value.userId;
		} else if (id === "txtReceiptId") {
			let value = event.target.value.toString();
			if (value.length >= 20) {
				value = value.slice(0, 20);
				enqueueSnackbar("Hai raggiunto il limite di caratteri", { variant: "info" });
			}
			currentSOS.receiptId = value;
		} else if (id === "txtMotivo") {
			currentSOS.motivo = event.target.value;
		} else if (id === "txtNote") {
			currentSOS.note = event.target.value;
		}
		setCurrentSOS(currentSOS);
	};
	const handleChangeDateSOS = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentSOS.date = date;
			setCurrentSOS((prevState) => ({ ...prevState, date: newDate }));
		}
	};
	const handleChangeDateRicUifSOS = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentSOS.dateRicUif = date;
			setCurrentSOS((prevState) => ({ ...prevState, dateRicUif: newDate }));
		}
	};
	const handleCancelSOS = () => {
		setCurrentSOS(new sosModel(
			0,
			new Date(),
			"",
			"",
			"",
			loggedUserId,
			new Date(),
			undefined,
			loggedUserId,
		));
		setSosAllegati([]);
		setPendingSosFiles([]);
	};
	const handleBtnAddSOSClick = async () => {
		const permissions = getSOSPermissions();
		if (!(currentSOS.id > 0 ? permissions.canUpdate : permissions.canCreate)) return;
		try {
			setBackDropInternalOpen(true);
			if (
				!currentSOS.motivo ||
				!currentSOS.date ||
				!currentSOS.receiptId ||
				!currentSOS.reportedByUser
			) {
				enqueueSnackbar(t("error.missingFields"), { variant: "error" });
				setBackDropInternalOpen(false);
				return;
			}
			//Se ne sto creando una
			if (currentSOS.id === 0) {
				const addSosResponse = await apiAddSos.fetch({
					fascicoloId: selFascicolo.id,
					date: currentSOS.date,
					motivo: currentSOS.motivo,
					note: currentSOS.note,
					receiptId: currentSOS.receiptId,
					reportedByUser: currentSOS.reportedByUser,
					dateRicUif: new Date(new Date(currentSOS.dateRicUif).setHours(12)),
				});
				if (addSosResponse.data.errors !== undefined) {
					enqueueSnackbar(t("error.generale"), { variant: "error" });
				} else {
					const result = addSosResponse.data.data.insert_sos_one;
					const filesToAttachOnSave = [...pendingSosFiles];
					let sosResultModel = new sosModel(
						result.Id,
						result.Date,
						result.Motivo,
						result.Note,
						result.ReceiptId,
						result.ReportedByUser,
						result.CreatedOn,
						result.ModifiedOn,
						result.CreatedBy,
						result.ModifiedBy,
						result.DateRicUif,
					);
					//Aggiungo la Audit per tracciare l'azione
					const newValues = sosResultModel;
					apiAddAudit.fetch({
						tenantId: tenantId,
						aziendaId: aziendaId,
						area: bsaSegnalazione,
						operation: "Create",
						entityType: "sos",
						entityId: result.Id.toString(),
						oldValues: "",
						newValues: JSON.stringify(newValues),
					});
					if (filesToAttachOnSave.length > 0) {
						await handleUploadFile("sos", result.Id)(filesToAttachOnSave);
					}
					setCurrentSOS(new sosModel(
						0,
						new Date(),
						"",
						"",
						"",
						loggedUserId,
						new Date(),
						undefined,
						loggedUserId,
					));
					const sosList = Object.assign([], currentSOSList);
					sosList.push(sosResultModel);
					setCurrentSOSList(sosList);
					setSosAllegati([]);
					setPendingSosFiles([]);
					enqueueSnackbar(t("message.success"), { variant: "success" });
				}
			}
			//altrimenti la sto modificando
			else {
				const updateSosResponse = await apiUpdateSos.fetch({
					id: currentSOS.id,
					date: currentSOS.date,
					motivo: currentSOS.motivo,
					note: currentSOS.note,
					receiptId: currentSOS.receiptId,
					reportedByUser: currentSOS.reportedByUser,
					dateRicUif: new Date(new Date(currentSOS.dateRicUif).setHours(12)),
				});
				if (updateSosResponse.data.errors !== undefined) {
					enqueueSnackbar(t("error.generale"), { variant: "error" });
				} else {
					const result = updateSosResponse.data.data.update_sos.returning[0];
					let sosResultModel = new sosModel(
						result.Id,
						result.Date,
						result.Motivo,
						result.Note,
						result.ReceiptId,
						result.ReportedByUser,
						result.CreatedOn,
						result.ModifiedOn,
						result.CreatedBy,
						result.ModifiedBy,
						result.DateRicUif,
					);
					//Aggiungo la Audit per tracciare l'azione
					const newValues = sosResultModel;
					apiAddAudit.fetch({
						tenantId: tenantId,
						aziendaId: aziendaId,
						area: bsaSegnalazione,
						operation: "Update",
						entityType: "sos",
						entityId: result.Id.toString(),
						oldValues: JSON.stringify(currentOriginalSOS),
						newValues: JSON.stringify(newValues),
					});
					setCurrentSOS(new sosModel(
						0,
						new Date(),
						"",
						"",
						"",
						loggedUserId,
						new Date(),
						undefined,
						loggedUserId,
					));
					setSosAllegati([]);
					enqueueSnackbar(t("message.success"), { variant: "success" });
				}
			}
			//Aggiorno la data di ultima modifica del fascicolo
			selFascicolo.aziendaId = aziendaId;
			selFascicolo.tenantId = tenantId;
			let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
				selFascicolo,
				selFascicolo.isPrivate,
			);
			if (!updatedFascicolo) {
				enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
				return;
			}
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "handleBtnAddSOSClick", "FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropInternalOpen(false);
			await handleReloadStatusFascicolo();
		}
	};
	const handleSelectedSOSClick = async (sos) => {
		try {
			let allAllegati = await allegatiApi.GetAllegati(itemId, tenantId, aziendaId);
			let allegatiSos = allAllegati.filter(allegato => allegato.entityType === 'sos' && allegato.entityId === sos.id);
			setSosAllegati(allegatiSos);
			setPendingSosFiles([]);
			setCurrentSOS(sos);
			setCurrentOriginalSOS(sos);
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"handleSelectedSOSClick","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	const handleDeleteSOSClick = (sos) => {
		if (!getSOSPermissions().canDelete) return;
		setIdToPerform(sos.id);
		setTypeToPerform("SOS");
		setShowDeleteModal(true);
	};
	const handlePrintDate = (date) => {
		if (date !== "" && isDateValid(date)) {
			let newDate = new Date(date);
			setPrintDate(newDate);
		} else {
			setPrintDate("");
		}
	};
	//#endregion
	//#region Modal Delete
	const handleModalDeleteClose = () => {
		setIdToPerform(0);
		setTypeToPerform("ALL");
		setShowDeleteModal(false);
	};
	const handleModalDeleteOkClick = async () => {
		const permissions = typeToPerform === "SOS" ? getSOSPermissions() : getFascicoloPermissions();
		if (!permissions.canDelete) return;
		try {
			if (idToPerform > 0) {
				setBackDropInternalOpen(true);
				if (typeToPerform === "ALL") {					
					//Rimuovo il file da Azure
					let fileName = currentAllegati.filter((x) => x.id === idToPerform)[0] ?.displayedName ??
						sosAllegati.filter((x) => x.id === idToPerform)[0] ?.displayedName ?? " ";						
					let deleteResp = await allegatiApi.DeleteAllegato(idToPerform,tenantId,aziendaId);
					if (deleteResp) {
						//Aggiorno l'elenco degli allegati
						logApi.AddLog(
							`DELETE FILE: ${fileName ?? "N/A"}; ID: ${idToPerform}; FascicoloID: ${itemId}; UtenteID: ${utentiList.filter((ut) => ut.userId === loggedUserId)[0]?.email};`,
							null,
							"handleModalDeleteOkClick",
							"FascicoloAdd",
							"INFO",
						);
						await loadAllegati(itemId);
						enqueueSnackbar(t("message.success"), { variant: "success" });
					}				
				} else if (typeToPerform === "SOS") {
					const response = await apiDeleteSos.fetch({ id: idToPerform });
					if (response.data.errors !== undefined) {
						enqueueSnackbar(t("error.generale"), { variant: "error" });
					} else {
						//Aggiorno l'elenco delle SOS
						const sos = Object.assign([], currentSOSList);
						const newSos = sos.filter((item) => item.id !== idToPerform);
						setCurrentSOSList(newSos);
						setCurrentSOS(new sosModel(
							0,
							new Date(),
							"",
							"",
							"",
							loggedUserId,
							new Date(),
							undefined,
							loggedUserId,
						));
						enqueueSnackbar(t("message.success"), { variant: "success" });
					}
				}
				//Aggiorno la data di ultima modifica del fascicolo
				selFascicolo.aziendaId = aziendaId;
				selFascicolo.tenantId = tenantId;
				let updatedFascicolo = await fascicoloApi.UpdateFascicolo(
					selFascicolo,
					selFascicolo.isPrivate,
				);
				if (!updatedFascicolo) {
					enqueueSnackbar("Errore durante l'aggiornamento del fascicolo", {variant: "error"});
					return;
				}
			}
		} catch (e) {
			logApi.AddLog(e.message,e.stack,"handleModalDeleteOkClick","FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setIdToPerform(0);
			setTypeToPerform("ALL");
			setShowDeleteModal(false);
			setBackDropInternalOpen(false);
			await handleReloadStatusFascicolo();
		}
	};
	//#endregion
	//#region Azioni che arrivano dal componente PrestazioneForm
	const handleGoToRischiPrestazione = (prestazione) => {
		setSelPrestazione(prestazione);
		setSelectedTab("tabRischio");
	};
	const handleReloadStatusFascicolo = async () => {
		if (selFascicolo && selFascicolo.id !== null && selFascicolo.id > 0) {
			try {
				const prestsByFascicolo = await apiPrestByFascicolo.fetch({
					fascicoloId: selFascicolo.id,
				});
				if (!prestsByFascicolo?.data?.data?.prestazione) {
					console.warn("Prestazioni non trovate per il fascicolo");
					return;
				}
				let allPrestOfFascicolo = prestsByFascicolo.data.data.prestazione;

				// Aggiorno la lista dei professionisti per la stampa
				let professionisti = [];
				for (let i = 0; i < allPrestOfFascicolo.length; i++) {
					const prestUsersList = allPrestOfFascicolo[i].prestazione_users || [];
					prestUsersList.forEach((prestUser) => {
						if (professionisti.filter((x) => x.userId === prestUser.UserId).length <= 0) {
							const utente = utentiList.find((x) => x.userId === prestUser.UserId);
							if (utente) professionisti.push(utente);
						}
					});
				}
				setFilteredUtentiList(professionisti);
				setActiveProfIds(getProfIdsWithActivePrestazioni(allPrestOfFascicolo));

				let isCessato = isFascicoloCessato(allPrestOfFascicolo);
				const fascicoloResponse = await apiFascicoloById.fetch({
					id: selFascicolo.id,
				});
				const item = fascicoloResponse?.data?.data?.fascicolo_by_pk;
				if (!item) {
					handleMissingFascicolo();
					return;
				}
				// Stato del fascicolo: fonte unica il backend (action getFascicoliAndRelatedEntities).
				// Il FE non ricalcola più lo stato lato client.
				const beFascicolo = (
					await fascicoloApi.GetFascicoliAndRelatedEntities(
						tenantId,
						true,
						true,
						selFascicolo.id,
					)
				)?.fascicoli?.[0];
				const { isCompleted, reasons } = mapFascicoloStatoFromBE(beFascicolo);
				if (isCessato) {
					setFascicoloCessato(isCessato);
				} else {
					setFascicoloCessato(isCessato);
					setStatusFascicolo(isCompleted);
					setStatusFascicoloReasons(reasons);
				}
				setSelFascicolo(new fascicoloModel(
					item.Id,
					item.TenantId,
					item.AziendaId,
					selFascicolo.name !== item.Name ? selFascicolo.name : item.Name,
					item.Date,
					item.IsPrivate,
					item.CreatedOn,
					item.ModifiedOn,
					[],
					isCompleted,
					reasons,
					item.CreatedBy,
					item.ModifiedBy,
					item.anagrafica_fascicolos ?? [],
				));
			} catch (e) {
				if (e.code === "FASCICOLO_NOT_FOUND") {
					handleMissingFascicolo();
					return;
				}
				logApi.AddLog(e?.message || String(e),e?.stack || "","loadStatusFsacicolo","FascicoloAdd");
			}
		}
	};
	//#endregion
	//EuroConference
	const handleEuroConfClick = async (itemId) => {
		try {
			setBackDropOpen(true);
			const openUrl = await euroConferenceApi.BuildEuroConferenceUrl(itemId);
			window.open(openUrl, "_blank", "noopener,noreferrer");
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "handleEuroConfClick", "FascicoloAdd");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};

	//#region Return Graphics
	return (
		<Fragment>
			<VaporPage
				title={selFascicolo.name}
				subtitle={
					selFascicolo.id === 0
						? t("fascicolo.titoloAdd")
						: selFascicolo.modifiedOn !== null
							? t("fascicolo.titoloManage") +
								" (ultima modifica: " +
								new Date(selFascicolo.modifiedOn).toLocaleDateString() +
								")"
							: t("fascicolo.titoloManage")
				}
				headerLeft={
					<Button
						variant="text"
						size="medium"
						color="primary"
						onClick={handleGoHome}
						sx={{ ml: 2 }}
					>
						{t("autovalutazione.autovalutazioneAdd.back")}
					</Button>
				}
				headerRight={
					isDataLoaded && fascicoloCessato ? (
						<div
							style={{
								...divRed,
								marginRight: "15px",
							}}
						>
							Cessato
						</div>
					) : isDataLoaded && statusFascicolo ? (
						<div
							style={{
								...(statusFascicolo === false ? divOrange : divGreen),
								marginRight: "15px",
							}}
						>
							{statusFascicolo === false ? "Incompleto" : "Completo"}
						</div>
					) : (
						<Tooltip
							title={
								<span style={{ whiteSpace: "pre-line" }}>
									{statusFascicoloReasons}
								</span>
							}
							arrow
							placement="left"
						>
							<div
								style={{
									...(statusFascicolo === false ? divOrange : divGreen),
									marginRight: "15px",
								}}
							>
								{statusFascicolo === false ? "Incompleto" : "Completo"}
							</div>
						</Tooltip>
					)
				}
			>
				<FascicoloDrawer
					tenantId={tenantId}
					openDrawer={openDrawer}
					drawerTabValue={drawerTabValue}
					drawerHeaderTitle={drawerHeaderTitle}
					drawerHeaderDesc={drawerHeaderDesc}
					handleDrawer={handleDrawer}
					handleDrawerTabChange={handleDrawerTabChange}
					openDrawerAction={openDrawerAction}
					backDropInternalOpen={backDropInternalOpen}
					selectedTab={selectedTab}
					userPermissions={userPermissions}
					userPermissionsSOS={userPermissionsSOS}
					utentiList={utentiList}
					selFascicolo={selFascicolo}
					hasUnavailableAllegati={hasUnavailableAllegati}
					tenantTypeId={tenantTypeId}
					bancaDatiTabValue={bancaDatiTabValue}
					bancaDatiResult={bancaDatiResult}
					currentAnagrafica={currentAnagrafica}
					hasBancaDatiAbb={hasBancaDatiAbb}
					bancaDatiAbbCredits={bancaDatiAbbCredits}
					bancaDatiAbbProductsList={bancaDatiAbbProductsList}
					bancaDatiAbbProductBought={bancaDatiAbbProductBought}
					currentAnagraficheList={currentAnagraficheList}
					anagraficheBancaDatiList={anagraficheBancaDatiList}
					handleChangeBancaDatiTab={handleChangeBancaDatiTab}
					handleCheckBancaDatiClick={handleCheckBancaDatiClick}
					handleDetailBancaDatiClick={handleDetailBancaDatiClick}
					handleBancaDatiBuyProduct={handleBancaDatiBuyProduct}
					allBrowserFiles={allBrowserFiles}
					currentAllegati={currentAllegati}
					handleEuroConfClick={handleEuroConfClick}
					handleRefreshAllegati={handleRefreshAllegati}
					handleUploadFile={handleUploadFile}
					handleDownloadAllegatoClick={handleDownloadAllegatoClick}
					handleDeleteAllegatoClick={handleDeleteAllegatoClick}
					printList={printList}
					selectedPrint={selectedPrint}
					filteredUtentiList={printUtentiList}
					selectedUsers={selectedUsers}
					printDate={printDate}
					printCessato={printCessato}
					printCessPrest={printCessPrest}
					prestazioniList={prestazioniList}
					rischiList={rischiList}
					selectedRischiToPrint={selectedRischiToPrint}
					filteredSOSList={filteredSOSList}
					selectedSOSToPrint={selectedSOSToPrint}
					selectedProfToPrint={selectedProfToPrint}
					indAnomaliaList={indAnomaliaList}
					selectedIndAnomaliaToPrint={selectedIndAnomaliaToPrint}
					handlePrintChange={handlePrintChange}
					handleUserChange={handleUserChange}
					handlePrintDate={handlePrintDate}
					handlePrintCessato={handlePrintCessato}
					handlePrestToPrintClick={handlePrestToPrintClick}
					handleLegalForReport={handleLegalForReport}
					handleRischiToPrintClick={handleRischiToPrintClick}
					handleSOSToPrintClick={handleSOSToPrintClick}
					handlePrintChangeDate={handlePrintChangeDate}
					handlePrintClick={handlePrintClick}
					handleIndAnomaliaToPrintClick={handleIndAnomaliaToPrintClick}
					currentSOS={currentSOS}
					sosAllegati={sosAllegati}
					currentSOSList={currentSOSList}
					pendingSosFiles={pendingSosFiles}
					handleChangeDateSOS={handleChangeDateSOS}
					handleChangeSOS={handleChangeSOS}
					handleChangeDateRicUifSOS={handleChangeDateRicUifSOS}
					handleCancelSOS={handleCancelSOS}
					handleBtnAddSOSClick={handleBtnAddSOSClick}
					handleDeleteSOSClick={handleDeleteSOSClick}
					handleSelectedSOSClick={handleSelectedSOSClick}
				/>
				<Loading open={backDropOpen} />
				{isDataLoaded && !userPermissions.canRead ? <NotAuth /> : null}
				<ModalBancaDatiDetail
					open={showModalDetail}
					onClose={handleModalClose}
					selBancaDatiItem={selBancaDatiItem}
					selBancaDatiItemKeys={selBancaDatiItemKeys}
					selBancaDatiItemValues={selBancaDatiItemValues}
					bancaDatiAbbResultListNeg={bancaDatiAbbResultListNeg}
					bancaDatiAbbResultListPar={bancaDatiAbbResultListPar}
					bancaDatiAbbResultListStr={bancaDatiAbbResultListStr}
				/>
				{showDeleteModal && idToPerform > 0 && typeToPerform !== "" ? (
					<ModalDelete
						show={showDeleteModal}
						onClose={handleModalDeleteClose}
						onOk={handleModalDeleteOkClick}
						deleteMessage={t("modal.deleteDesc")}
					></ModalDelete>
				) : null}
				<ModalPdfViewer
					show={pdfDocuments.length > 0}
					documents={pdfDocuments}
					activeDocumentId={activePdfDocumentId}
					onSelectDocument={(docId) => setActivePdfDocumentId(docId)}
					onCloseDocument={(docId) => {
						const remaining = pdfDocuments.filter(d => d.id !== docId);
						setPdfDocuments(remaining);
						if (activePdfDocumentId === docId) {
							setActivePdfDocumentId(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
						}
					}}
					onClose={() => { setPdfDocuments([]); setActivePdfDocumentId(null); }}
				/>
				{isDataLoaded && userPermissions.canRead ? (
					<VaporPage.Section>
						<Grid container>
							<Grid item xs={12}>
								{selFascicolo.id > 0 ? (
									<Grid item xs={12} sx={{ mt: 0 }}>
										<ExtendedTabs
											variant="standard"
											size="small"
											value={selectedTab}
											onChange={handleChangeTab}
											sx={{ mb: 0 }}
										>
											<ExtendedTab
												value="tabAnagrafica"
												label={t("fascicolo.form.tabAnagrafica")}
											/>
											<ExtendedTab
												value="tabPrestazione"
												label={t("fascicolo.form.tabPrestazioni")}
											/>
											<ExtendedTab
												value="tabRischio"
												label={t("fascicolo.form.tabRischio")}
											/>
											<ExtendedTab
												value="tabIndAnomalia"
												label={t(
													"fascicolo.fascicoloAdd.indicatoriAnomaliaLabel",
												)}
											/>
										</ExtendedTabs>
										{selectedTab === "tabAnagrafica" ? (
											<Grid container>
												<Grid item xs={12}>
													<AnagraficaForm
														fascicolo={selFascicolo}
														reloadStatusFascicolo={handleReloadStatusFascicolo}
														selectedValue={selectedValue}
													/>
												</Grid>
											</Grid>
										) : null}
										{selectedTab === "tabPrestazione" ? (
											<Grid container>
												<Grid item xs={12}>
													<PrestazioneForm
														fascicolo={selFascicolo}
														onRischio={handleGoToRischiPrestazione}
														reloadStatusFascicolo={handleReloadStatusFascicolo}
													/>
												</Grid>
											</Grid>
										) : null}
										{selectedTab === "tabRischio" ? (
											<Grid container>
												<Grid item xs={12}>
													<NewRischioForm
														fascicolo={selFascicolo}
														reloadStatusFascicolo={handleReloadStatusFascicolo}
														prestazionePreselected={selPrestazione}
													/>
												</Grid>
											</Grid>
										) : null}
										{selectedTab === "tabIndAnomalia" ? (
											<Grid container>
												<Grid item xs={12}>
													<IndicatoreAnomaliaForm
														fascicolo={selFascicolo}
														reloadStatusFascicolo={handleReloadStatusFascicolo}
													/>
												</Grid>
											</Grid>
										) : null}
									</Grid>
								) : null}
							</Grid>
						</Grid>
					</VaporPage.Section>
				) : null}
			</VaporPage>
		</Fragment>
	);
	//#endregion
};
